import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, ArrowLeft, Save, Check, X, Search, Link2, AlertTriangle,
  Package, Euro, Plus, Building2, Sparkles, Hash, Loader2, Info, Pencil, Scale, Utensils
} from "lucide-react";
import { Input } from "../../components/ui/input";
import DateInput from "../../components/DateInput";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Normalizza l'UdM di fattura in gruppi: cartone→"CT", pezzo/confezione→"PZ".
const normUmFattura = (um) => {
  const u = (um || "").trim().toUpperCase().replace(/\.$/, "");
  if (["CT", "CRT", "CAR", "CARTONE", "CARTON", "COLLO", "COLLI"].includes(u)) return "CT";
  if (["PZ", "CF", "NR", "N", "PEZZI", "PEZZO", "CONF", "CONFEZIONE"].includes(u)) return "PZ";
  return u || "_default";
};

// Risolve la conversione appresa per un gruppo dato l'UdM della riga fattura.
// Priorità: (1) match esatto UdM → (2) derivazione via pz_per_ct (1 CT = N × PZ)
// → (3) legacy default → (4) retro-compat: se c'è UNA sola conversione, applicala
//    comunque (comportamento del sistema precedente, evita regressioni sul caso base).
const resolveLearned = (group, umFattura) => {
  if (!group) return null;
  // Retro-compat forte: se il backend/dati restituiscono la vecchia voce "piatta"
  // ({fattore, um_target}) invece del gruppo per UdM, applicala direttamente
  // (comportamento del sistema precedente). Evita la regressione "non applica nulla".
  if (typeof group.fattore === "number") {
    return group.fattore > 0 ? group : null;
  }
  const um = normUmFattura(umFattura);
  if (group[um] && group[um].fattore > 0) return group[um];
  const pz = group.PZ;
  const ct = group.CT;
  const ratio = group.pz_per_ct;
  if (um === "CT" && pz && pz.fattore > 0 && ratio > 0) {
    return { fattore: pz.fattore * ratio, um_target: pz.um_target, derived: true };
  }
  if (um !== "CT" && ct && ct.fattore > 0 && ratio > 0) {
    return { fattore: ct.fattore / ratio, um_target: ct.um_target, derived: true };
  }
  if (group._default && group._default.fattore > 0 && um !== "CT") return group._default;
  // Retro-compat per FAMIGLIA: riga cartone → solo voce CT; riga pezzo → voce pezzo/default.
  // Così il caso base (solo PZ appreso) torna a funzionare, senza applicare un fattore
  // pezzo a una riga cartone (o viceversa) quando manca il rapporto pezzi/cartone.
  if (um === "CT") {
    return ct && ct.fattore > 0 ? ct : null;
  }
  const pieceKeys = Object.keys(group).filter(
    (k) => k !== "pz_per_ct" && k !== "CT" && group[k] && group[k].fattore > 0
  );
  if (pieceKeys.length >= 1) return group[pieceKeys[0]];
  return null;
};

const RevisioneFatturaSDI = () => {
  const { getAuthHeaders, localeCorrente } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fattura, setFattura] = useState(null);
  const [righe, setRighe] = useState([]);
  const [autoImportEseguito, setAutoImportEseguito] = useState(false);
  const [giaImportata, setGiaImportata] = useState(false);
  const [aggiornaIvaImport, setAggiornaIvaImport] = useState(true);
  
  // Dati per associazione
  const [ingredienti, setIngredienti] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [materiali, setMateriali] = useState([]);
  const [categorieMateriali, setCategorieMateriali] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [fornitoreSelezionato, setFornitoreSelezionato] = useState(null);
  const [codiciFornitore, setCodiciFornitore] = useState({}); // Mappa codice -> ingrediente
  // Conversioni apprese (correzioni manuali memorizzate): { byCodice, byDesc }
  const conversioniRef = useRef({ byCodice: {}, byDesc: {} });
  
  // Dialog associazione ingrediente
  const [associaDialogOpen, setAssociaDialogOpen] = useState(false);
  const [rigaCorrente, setRigaCorrente] = useState(null);
  const [searchIngrediente, setSearchIngrediente] = useState("");
  const [tipoProdottoDialog, setTipoProdottoDialog] = useState("food"); // "food" | "materiale"
  
  // Dialog selezione fornitore
  const [fornitoreDialogOpen, setFornitoreDialogOpen] = useState(false);
  const [searchFornitore, setSearchFornitore] = useState("");
  const [creatingFornitore, setCreatingFornitore] = useState(false);
  
  // Dialog nuovo ingrediente
  const [nuovoIngredienteDialogOpen, setNuovoIngredienteDialogOpen] = useState(false);
  const [nuovoIngrediente, setNuovoIngrediente] = useState({
    nome: "",
    categoria_id: "",
    unita_misura: "kg"
  });
  const [creatingIngrediente, setCreatingIngrediente] = useState(false);

  // Dialog nuovo materiale non-food
  const [nuovoMaterialeDialogOpen, setNuovoMaterialeDialogOpen] = useState(false);
  const [nuovoMateriale, setNuovoMateriale] = useState({ nome: "", categoria_id: "", unita_misura: "pz", tipo: "packaging" });
  const [creatingMateriale, setCreatingMateriale] = useState(false);
  
  // Impostazioni
  const [dataFattura, setDataFattura] = useState("");
  const [aggiornaPrezzo, setAggiornaPrezzo] = useState(true);
  const [confirmImportOpen, setConfirmImportOpen] = useState(false); // Modal conferma
  const [fornitoreMissingOpen, setFornitoreMissingOpen] = useState(false); // Alert fornitore mancante
  const pendingImportRef = useRef(false); // Flag per procedere al confirm dopo selezione fornitore

  // Associa fornitore selezionato + aggiorna P.IVA se mancante
  const handleAssociaFornitore = useCallback(async (f) => {
    setFornitoreDialogOpen(false);
    const nome = f.nome || f.ragione_sociale || "";
    
    // Se il fornitore non ha P.IVA ma la fattura ce l'ha → aggiorna automaticamente
    const pivaFattura = (fattura?.sender?.vat_code || "").trim();
    if (pivaFattura && !f.piva) {
      try {
        await axios.put(`${API}/fornitori/${f.id}`, { piva: pivaFattura }, { headers: getAuthHeaders() });
        f = { ...f, piva: pivaFattura };
        setFornitori(prev => prev.map(x => x.id === f.id ? { ...x, piva: pivaFattura } : x));
        toast.success(`Fornitore selezionato: ${nome} — P.IVA salvata per riconoscimento automatico`);
      } catch {
        toast.success(`Fornitore selezionato: ${nome}`);
      }
    } else {
      toast.success(`Fornitore selezionato: ${nome}`);
    }

    setFornitoreSelezionato(f);

    // Carica codici fornitore per auto-match
    const localeId = localeCorrente?.id || "";
    try {
      const codiciRes = await axios.get(
        `${API}/sdi/codici-fornitore/${f.id}?locale_id=${localeId}`,
        { headers: getAuthHeaders() }
      );
      const nuoviCodici = codiciRes.data.codici || {};
      setCodiciFornitore(nuoviCodici);
      try {
        const convRes = await axios.get(`${API}/sdi/conversioni-apprese/${f.id}`, { headers: getAuthHeaders() });
        conversioniRef.current = { byCodice: convRes.data.byCodice || {}, byDesc: convRes.data.byDesc || {} };
      } catch { /* nessuna conversione appresa */ }
      autoMatchConCodici(righe, ingredienti, nuoviCodici);
    } catch {
      /* nessun codice salvato, va bene */
    }
    // Se provenivamo dall'alert "fornitore mancante", procedi al confirm
    if (pendingImportRef.current) {
      pendingImportRef.current = false;
      setConfirmImportOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fattura, localeCorrente, getAuthHeaders, righe, ingredienti]);

  // Crea nuovo fornitore dalla fattura corrente
  const handleCreaFornitore = useCallback(async () => {
    if (!fattura?.sender) return;
    setCreatingFornitore(true);
    try {
      const nomeNew = fattura.sender.name || "Fornitore";
      const pivaNew = fattura.sender.vat_code || "";
      const localeId = localeCorrente?.id || "";
      const res = await axios.post(`${API}/fornitori`, {
        ragione_sociale: nomeNew,
        nome: nomeNew,
        piva: pivaNew,
        locali_ids: localeId ? [localeId] : [],
        categoria: "altro"
      }, { headers: getAuthHeaders() });
      const nuovo = { ...res.data, nome: res.data.nome || res.data.ragione_sociale };
      setFornitori(prev => [...prev, nuovo]);
      setFornitoreDialogOpen(false);
      setFornitoreSelezionato(nuovo);
      toast.success(`Fornitore "${nomeNew}" creato e selezionato`);
      // Se provenivamo dall'alert "fornitore mancante", procedi al confirm
      if (pendingImportRef.current) {
        pendingImportRef.current = false;
        setConfirmImportOpen(true);
      }
    } catch (e) {
      toast.error("Errore nella creazione del fornitore");
    } finally {
      setCreatingFornitore(false);
    }
  }, [fattura, localeCorrente, getAuthHeaders]);

  // Avviso prima di abbandonare la pagina con associazioni non importate
  useEffect(() => {
    const haveAssociazioni = righe.some(r => r.associato && !r.escluso);
    if (!haveAssociazioni || saving) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "Hai associazioni non importate. Se esci ora andranno perse.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [righe, saving]);

  // Carica dati iniziali
  useEffect(() => {
    const loadData = async () => {
      try {
        // Carica fattura da sessionStorage
        const fatturaData = sessionStorage.getItem("sdi_fattura_data");
        if (!fatturaData) {
          toast.error("Nessuna fattura da processare");
          navigate("/acquisti/import-sdi");
          return;
        }
        
        const parsedFattura = JSON.parse(fatturaData);
        setFattura(parsedFattura);
        setDataFattura(parsedFattura.issue_date || new Date().toISOString().split("T")[0]);
        
        // Pattern di spese accessorie / righe tecniche da auto-escludere
        // (non sono materie prime ma costi accessori della fattura)
        const PATTERNS_SPESE_ACCESSORIE = [
          /spes[ae]?\s+(?:di\s+)?trasport/i,
          /^\s*trasporto\s*$/i,
          /spes[ae]?\s+(?:di\s+)?conse?gn/i,
          /spes[ae]?\s+accesso/i,
          /spes[ae]?\s+amministrat/i,
          /spes[ae]?\s+banca/i,
          /spes[ae]?\s+ambient/i,
          /spes[ae]?\s+(?:di\s+)?fattur/i,
          /spes[ae]?\s+(?:di\s+)?bollo/i,
          /spes[ae]?\s+fiss/i,
          /\boner[ie]\b/i,
          /\bbollo\b/i,
          /\bcauzion/i,
          /\bvuoto\s+a\s+rend/i,
          /contribut[oi]\s+conai/i,
          /\bconai\b/i,
          /\bimball/i,
          /\bpallet\b/i,
          /\bporto\s+franco\b/i,
          /\bdirit[ti]\s+(?:fis|amm)/i,
          /\bsconto\b/i,
          /\babbuono\b/i,
          /\barrotonda/i,
          // Righe tecniche / di servizio
          /riga\s+ausiliar/i,
          /informazioni\s+(?:tecniche|aggiunt)/i,
          /\bdati\s+aggiunt/i,
        ];
        const isSpesaAccessoria = (desc) => {
          if (!desc) return false;
          return PATTERNS_SPESE_ACCESSORIE.some(p => p.test(desc));
        };

        // Righe già importate da un auto-import parziale precedente (da escludere)
        let importedKeys = new Set();
        try {
          if (parsedFattura.document_number && localeCorrente?.id) {
            const rkRes = await axios.get(
              `${API}/sdi/righe-importate?numero_fattura=${encodeURIComponent(parsedFattura.document_number)}&locale_id=${localeCorrente.id}`,
              { headers: getAuthHeaders() }
            );
            importedKeys = new Set(rkRes.data.keys || []);
          }
        } catch (e) { /* nessuna riga già importata */ }

        const rigaGiaImportata = (line) => {
          const cod = (line.item_code || "").trim().toUpperCase();
          const dn = (line.description || "").trim().toUpperCase().replace(/\s+/g, " ");
          return (cod && importedKeys.has(`COD:${cod}`)) || (dn && importedKeys.has(`DESC:${dn}`));
        };

        // Prepara le righe con campi editabili
        // Auto-escludi le righe che sono "spese accessorie" (trasporto, imballo, conai, ecc.)
        const righePreparate = (parsedFattura.lines || []).map((line, idx) => {
          const isSpesa = isSpesaAccessoria(line.description || "");
          // Importo riga sotto 1€ (incluso 0) → mai selezionabile per l'import
          const importoRiga = parseFloat(line.total_price ?? line.unit_price ?? 0) || 0;
          const isImportoBasso = importoRiga < 1;
          const giaImp = rigaGiaImportata(line);
          const autoEscluso = isSpesa || isImportoBasso || giaImp;
          const motivoAuto = giaImp ? "Già importata" : (isSpesa ? "Spesa accessoria" : (isImportoBasso ? "Importo sotto 1€" : null));
          return {
            ...line,
            id: `line-${idx}`,
            descrizione_originale: line.description,
            quantita_originale: line.quantity,
            prezzo_originale: line.unit_price,
            codice_articolo: line.item_code || null,
            ingrediente_id: null,
            ingrediente_nome: null,
            quantita_kg: null,
            prezzo_kg: null,
            associato: false,
            escluso: autoEscluso,
            escluso_auto: autoEscluso,             // marker: esclusione automatica
            motivo_esclusione_auto: motivoAuto,
            importato: giaImp,                     // riga già importata da auto-import parziale
            match_type: null,
          };
        });
        setRighe(righePreparate);
        
        const localeId = localeCorrente?.id || "";
        const localeParam = localeId ? `locale_id=${localeId}` : "";
        
        // Usa Promise.allSettled: se un singolo endpoint fallisce (es. /materiali/categorie
        // 500 in produzione), gli altri continuano a caricare correttamente
        // → l'utente vede comunque la lista delle materie prime nella dialog "Associa".
        const endpointDefs = [
          { key: "ingredienti", url: `${API}/ingredienti${localeParam ? `?${localeParam}` : ""}`, setter: setIngredienti },
          { key: "fornitori", url: `${API}/fornitori${localeParam ? `?${localeParam}` : ""}`, setter: setFornitori },
          { key: "categorie-ingredienti", url: `${API}/categorie-ingredienti${localeParam ? `?${localeParam}` : ""}`, setter: setCategorie },
          { key: "materiali", url: `${API}/materiali${localeParam ? `?${localeParam}` : ""}`, setter: setMateriali },
          { key: "materiali/categorie", url: `${API}/materiali/categorie${localeParam ? `?${localeParam}` : ""}`, setter: setCategorieMateriali },
        ];
        const results = await Promise.allSettled(
          endpointDefs.map((e) => axios.get(e.url, { headers: getAuthHeaders() }))
        );
        const failedEndpoints = [];
        results.forEach((r, i) => {
          const def = endpointDefs[i];
          if (r.status === "fulfilled") {
            def.setter(r.value.data || []);
          } else {
            def.setter([]);
            failedEndpoints.push(def.key);
            console.error(`[RevisioneFatturaSDI] Endpoint ${def.key} failed:`, r.reason?.response?.status, r.reason?.message);
          }
        });
        if (failedEndpoints.length > 0) {
          toast.error(`Errore nel caricamento: ${failedEndpoints.join(", ")}`);
        }
        // Alias per uso locale nel resto della funzione
        const ingRes = results[0].status === "fulfilled" ? results[0].value : { data: [] };
        const fornRes = results[1].status === "fulfilled" ? results[1].value : { data: [] };
        
        // ── Riconoscimento fornitore ───────────────────────────────────────
        const pivaFattura = (parsedFattura.sender?.vat_code || "").replace(/^IT/i, "");
        const nomeFattura = (parsedFattura.sender?.name || "").toLowerCase().trim();
        let fornitore = null;
        
        if (pivaFattura) {
          fornitore = (fornRes.data || []).find(f =>
            (f.piva || "").replace(/^IT/i, "") === pivaFattura
          );
        }
        if (!fornitore && nomeFattura) {
          fornitore = (fornRes.data || []).find(f => {
            const db = (f.nome || f.ragione_sociale || "").toLowerCase();
            return db.includes(nomeFattura) || nomeFattura.includes(db);
          });
        }
        
        if (fornitore) {
          setFornitoreSelezionato(fornitore);
          toast.success(`Fornitore identificato: ${fornitore.nome || fornitore.ragione_sociale}`);

          // 1. Match per codice fornitore (più affidabile)
          let codiciMap = {};
          try {
            const codiciRes = await axios.get(
              `${API}/sdi/codici-fornitore/${fornitore.id}?locale_id=${localeId}`,
              { headers: getAuthHeaders() }
            );
            codiciMap = codiciRes.data.codici || {};
            setCodiciFornitore(codiciMap);
          } catch { /* ignora */ }

          // 1b. Carica conversioni apprese (correzioni manuali memorizzate)
          try {
            const convRes = await axios.get(
              `${API}/sdi/conversioni-apprese/${fornitore.id}`,
              { headers: getAuthHeaders() }
            );
            conversioniRef.current = { byCodice: convRes.data.byCodice || {}, byDesc: convRes.data.byDesc || {} };
          } catch { conversioniRef.current = { byCodice: {}, byDesc: {} }; }

          // 2. Match per descrizione memorizzata (learning loop) — solo righe SENZA codice
          let descMatchMap = {};
          try {
            const descriptions = righePreparate
              .filter(r => !r.escluso && r.description)
              .map(r => r.description);
            if (descriptions.length > 0) {
              const descRes = await axios.post(
                `${API}/sdi/match-descrizioni`,
                { fornitore_id: fornitore.id, descrizioni: descriptions },
                { headers: getAuthHeaders() }
              );
              descMatchMap = descRes.data.results || {};
            }
          } catch { /* ignora */ }

          // Esegui il match a cascata: codici → descrizioni memorizzate → fuzzy nome
          autoMatchCascata(righePreparate, ingRes.data || [], codiciMap, descMatchMap);
        } else {
          // Fornitore non trovato — l'utente dovrà selezionarlo manualmente dal dialog
          autoMatchIngredienti(righePreparate, ingRes.data || []);
        }

        setLoading(false);
      } catch (error) {
        console.error("Errore caricamento dati:", error);
        toast.error("Errore caricamento dati");
        setLoading(false);
      }
    };
    
    loadData();
  }, [localeCorrente, getAuthHeaders, navigate]);

  // Verifica se questa fattura risulta GIÀ IMPORTATA (fonte di verità: backend)
  useEffect(() => {
    if (!fattura || !localeCorrente?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const payload = {
          fatture: [{
            invoice_id: fattura.id,
            sender_name: fattura.sender?.name || null,
            sender_vat_code: fattura.sender?.vat_code || null,
            document_number: fattura.document_number || null,
          }],
          locale_id: localeCorrente.id,
        };
        const res = await axios.post(`${API}/sdi/match-fornitori-batch`, payload, { headers: getAuthHeaders() });
        if (cancelled) return;
        setGiaImportata(!!(res.data.results?.[0]?.imported));
      } catch (e) {
        console.warn("Verifica fattura importata fallita", e);
      }
    })();
    return () => { cancelled = true; };
  }, [fattura, localeCorrente?.id, getAuthHeaders]);


  // Match a cascata: codice fornitore → descrizione memorizzata (learning loop) → fuzzy nome
  function autoMatchCascata(righeData, ingredientiData, codiciMap, descMatchMap) {
    let matchesCodice = 0;
    let matchesDescrizione = 0;
    let matchesNome = 0;

    const righeAggiornate = righeData.map(riga => {
      // Salta righe escluse (es. spese accessorie auto-escluse)
      if (riga.escluso) return riga;

      // 1. Match per codice articolo (priorità massima)
      if (riga.codice_articolo) {
        const codiceUpper = riga.codice_articolo.toUpperCase().trim();
        const match = codiciMap[codiceUpper];
        if (match) {
          const ingrediente = ingredientiData.find(i => i.id === match.ingrediente_id);
          if (ingrediente) {
            matchesCodice++;
            const quantitaKg = calcolaQuantitaKg(riga.quantity, riga.unit_of_measure, riga.description, ingrediente.unita_misura, riga.codice_articolo);
            const prezzoKg = quantitaKg > 0 ? riga.total_price / quantitaKg : riga.unit_price;
            return {
              ...riga,
              ingrediente_id: ingrediente.id,
              ingrediente_nome: ingrediente.nome,
              quantita_kg: quantitaKg,
              prezzo_kg: prezzoKg,
              associato: true,
              match_type: 'codice',
              match_score: 100,
            };
          }
        }
      }

      // 2. Match per descrizione memorizzata (learning loop)
      const descNorm = (riga.description || "").trim().toUpperCase().replace(/\s+/g, ' ');
      const descMatch = descMatchMap[descNorm];
      if (descMatch?.ingrediente_id) {
        const ingrediente = ingredientiData.find(i => i.id === descMatch.ingrediente_id);
        if (ingrediente) {
          matchesDescrizione++;
          const quantitaKg = calcolaQuantitaKg(riga.quantity, riga.unit_of_measure, riga.description, ingrediente.unita_misura, riga.codice_articolo);
          const prezzoKg = quantitaKg > 0 ? riga.total_price / quantitaKg : riga.unit_price;
          return {
            ...riga,
            ingrediente_id: ingrediente.id,
            ingrediente_nome: ingrediente.nome,
            quantita_kg: quantitaKg,
            prezzo_kg: prezzoKg,
            associato: true,
            match_type: 'descrizione',
            match_score: 95,
          };
        }
      }

      // 3. Fuzzy match per nome (fallback)
      const descPulita = pulisciDescrizione(riga.description);
      let bestMatch = null;
      let bestScore = 0;
      for (const ing of ingredientiData) {
        const ingNomePulito = pulisciDescrizione(ing.nome);
        const score = calcolaSimiliarieta(descPulita, ingNomePulito);
        if (score > bestScore && score >= 60) {
          bestScore = score;
          bestMatch = ing;
        }
      }
      if (bestMatch) {
        matchesNome++;
        const quantitaKg = calcolaQuantitaKg(riga.quantity, riga.unit_of_measure, riga.description, bestMatch.unita_misura, riga.codice_articolo);
        const prezzoKg = quantitaKg > 0 ? riga.total_price / quantitaKg : riga.unit_price;
        return {
          ...riga,
          ingrediente_id: bestMatch.id,
          ingrediente_nome: bestMatch.nome,
          quantita_kg: quantitaKg,
          prezzo_kg: prezzoKg,
          associato: true,
          match_type: 'nome',
          match_score: bestScore,
        };
      }

      return riga;
    });

    setRighe(righeAggiornate);

    const totale = matchesCodice + matchesDescrizione + matchesNome;
    if (totale > 0) {
      const parts = [];
      if (matchesCodice > 0) parts.push(`${matchesCodice} per codice`);
      if (matchesDescrizione > 0) parts.push(`${matchesDescrizione} memorizzati`);
      if (matchesNome > 0) parts.push(`${matchesNome} per nome`);
      toast.info(`Auto-associati ${totale} prodotti (${parts.join(", ")})`);
    }
  }

  // Auto-match usando i codici fornitore (priorità massima)
  // NOTA: function declaration (non const) per hoisting → evita TDZ quando referenziata in useCallback/useEffect sopra
  function autoMatchConCodici(righeData, ingredientiData, codiciMap) {
    let matchesCodice = 0;
    let matchesNome = 0;
    
    const righeAggiornate = righeData.map(riga => {
      // 1. Prima prova con il codice articolo
      if (riga.codice_articolo) {
        const codiceUpper = riga.codice_articolo.toUpperCase().trim();
        const match = codiciMap[codiceUpper];
        if (match) {
          const ingrediente = ingredientiData.find(i => i.id === match.ingrediente_id);
          if (ingrediente) {
            matchesCodice++;
            const quantitaKg = calcolaQuantitaKg(riga.quantity, riga.unit_of_measure, riga.description, ingrediente.unita_misura, riga.codice_articolo);
            const prezzoKg = quantitaKg > 0 ? riga.total_price / quantitaKg : riga.unit_price;
            
            return {
              ...riga,
              ingrediente_id: ingrediente.id,
              ingrediente_nome: ingrediente.nome,
              quantita_kg: quantitaKg,
              prezzo_kg: prezzoKg,
              associato: true,
              match_type: 'codice',
              match_score: 100
            };
          }
        }
      }
      
      // 2. Se non trova con codice, prova con fuzzy matching nome
      const descPulita = pulisciDescrizione(riga.description);
      let bestMatch = null;
      let bestScore = 0;
      
      for (const ing of ingredientiData) {
        const ingNomePulito = pulisciDescrizione(ing.nome);
        const score = calcolaSimiliarieta(descPulita, ingNomePulito);
        
        if (score > bestScore && score >= 60) {
          bestScore = score;
          bestMatch = ing;
        }
      }
      
      if (bestMatch) {
        matchesNome++;
        const quantitaKg = calcolaQuantitaKg(riga.quantity, riga.unit_of_measure, riga.description, bestMatch.unita_misura, riga.codice_articolo);
        const prezzoKg = quantitaKg > 0 ? riga.total_price / quantitaKg : riga.unit_price;
        
        return {
          ...riga,
          ingrediente_id: bestMatch.id,
          ingrediente_nome: bestMatch.nome,
          quantita_kg: quantitaKg,
          prezzo_kg: prezzoKg,
          associato: true,
          match_type: 'nome',
          match_score: bestScore
        };
      }
      
      return riga;
    });
    
    setRighe(righeAggiornate);
    
    // Mostra statistiche
    const totaleMatches = matchesCodice + matchesNome;
    if (totaleMatches > 0) {
      let msg = `Auto-associati ${totaleMatches} prodotti`;
      if (matchesCodice > 0) msg += ` (${matchesCodice} per codice)`;
      toast.info(msg);
    }
  };

  // Auto-match ingredienti basato solo sul nome (fallback)
  function autoMatchIngredienti(righeData, ingredientiData) {
    const righeAggiornate = righeData.map(riga => {
      const descPulita = pulisciDescrizione(riga.description);
      let bestMatch = null;
      let bestScore = 0;
      
      for (const ing of ingredientiData) {
        const ingNomePulito = pulisciDescrizione(ing.nome);
        const score = calcolaSimiliarieta(descPulita, ingNomePulito);
        
        if (score > bestScore && score >= 60) {
          bestScore = score;
          bestMatch = ing;
        }
      }
      
      if (bestMatch) {
        const quantitaKg = calcolaQuantitaKg(riga.quantity, riga.unit_of_measure, riga.description, bestMatch.unita_misura, riga.codice_articolo);
        const prezzoKg = quantitaKg > 0 ? riga.total_price / quantitaKg : riga.unit_price;
        
        return {
          ...riga,
          ingrediente_id: bestMatch.id,
          ingrediente_nome: bestMatch.nome,
          quantita_kg: quantitaKg,
          prezzo_kg: prezzoKg,
          associato: true,
          match_type: 'nome',
          match_score: bestScore
        };
      }
      
      return riga;
    });
    
    setRighe(righeAggiornate);
    
    const matches = righeAggiornate.filter(r => r.associato).length;
    if (matches > 0) {
      toast.info(`Auto-associati ${matches} prodotti su ${righeAggiornate.length}`);
    }
  };

  // Pulisce la descrizione per il matching
  function pulisciDescrizione(desc) {
    if (!desc) return "";
    return desc
      .toLowerCase()
      .replace(/[0-9]+\s*(g|kg|ml|l|pz|ct|cf)\b/gi, "")
      .replace(/\d+x\d+/gi, "")
      .replace(/[^\w\s]/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  // Calcola similarità tra due stringhe
  function calcolaSimiliarieta(str1, str2) {
    if (!str1 || !str2) return 0;
    
    const words1 = str1.split(" ").filter(w => w.length > 2);
    const words2 = str2.split(" ").filter(w => w.length > 2);
    
    if (words1.length === 0 || words2.length === 0) return 0;
    
    let matches = 0;
    for (const w1 of words1) {
      for (const w2 of words2) {
        if (w1.includes(w2) || w2.includes(w1)) {
          matches++;
          break;
        }
      }
    }
    
    return Math.round((matches / Math.max(words1.length, words2.length)) * 100);
  };

  // Estrae il numero di pezzi per pacco da una descrizione fattura
  // Mirror della logica backend confezionamento_parser.estrai_pezzi_per_pacco
  function estraiPezziPerPacco(description) {
    const descUp = (description || "").toUpperCase().trim();

    // 1. "Pz.18x30Gr" / "PZ.18×30" → 18 pezzi per pacco
    const m1 = descUp.match(/\bPZ\.?\s*(\d+)\s*[xX×*·]\s*\d/);
    if (m1) { const n = parseInt(m1[1]); if (n > 1 && n <= 1000) return n; }

    // 1b. "12CF*18PZ" / "34CONF×18PZ" → 12×18 = pezzi totali
    const m1b = descUp.match(/(\d+)\s*(?:CF|BUS|BUST|BUSTA|CONF|SAC|SACC|SCAT|COL|COLLO)\s*[xX×*·]\s*(\d+)\s*PZ\b/);
    if (m1b) { const n = parseInt(m1b[1]) * parseInt(m1b[2]); if (n > 1 && n <= 10000) return n; }

    // 2. "NxM PZ" / "N*M PZ" → pezzi totali del pacco (es. "1x16x18 PZ")
    const m2 = descUp.match(/(\d+)\s*[xX×*·]\s*(\d+)\s*PZ\b/);
    if (m2) { const n = parseInt(m2[1]) * parseInt(m2[2]); if (n > 1 && n <= 10000) return n; }

    // 3. "N PZ" semplice (es. "12 Pz")
    const m3 = descUp.match(/\b(\d+)\s*PZ\b/);
    if (m3) { const n = parseInt(m3[1]); if (n > 1 && n <= 1000) return n; }

    // 4. "Conf.N" / "Cartone da N pezzi"
    const m4 = descUp.match(/\b(?:CONF\.?|CART(?:ONE)?\s+DA|DA)\s*(\d+)\b/);
    if (m4) { const n = parseInt(m4[1]); if (n > 1 && n <= 1000) return n; }

    // 5. "N/M<unità>" / "NxM<unità>" → N bottiglie per pacco
    //    es. "24/33 CL" → 24, "24X33CL" → 24, "6/1.5LT" → 6, "12X1L" → 12
    const m5 = descUp.match(/(\d+)\s*[xX×/÷*·]\s*\d+(?:[,.]\d+)?\s*(?:KG|GR|G|ML|CL|LT|L)\b/);
    if (m5) { const n = parseInt(m5[1]); if (n > 1 && n <= 1000) return n; }

    return null;
  }

  // Calcola quantità + um_riferimento (mirror della logica backend calcola_qta_e_prezzo)
  // Ritorna { qta, um } dove um ∈ {"kg", "lt", "pz"}
  function calcolaQuantitaUm(quantity, um, description, umTarget = null, codice = null) {
    if (!quantity) return { qta: 0, um: "pz" };

    // 0pre. CONVERSIONE APPRESA: se l'utente ha già corretto questo prodotto, riapplica il fattore.
    // Priorità: codice articolo → descrizione normalizzata. Vince su qualsiasi parsing automatico.
    const conv = conversioniRef.current || { byCodice: {}, byDesc: {} };
    const descKey = (description || "").trim().toUpperCase().replace(/\s+/g, " ");
    const codiceKey = (codice || "").trim().toUpperCase();
    const group = (codiceKey && conv.byCodice[codiceKey]) || (descKey && conv.byDesc[descKey]);
    const learned = resolveLearned(group, um);
    if (learned && learned.fattore > 0) {
      return { qta: Number(quantity) * learned.fattore, um: learned.um_target || umTarget || "kg", learned: true };
    }

    const umUp = (um || "").toUpperCase().trim();
    const descUp = (description || "").toUpperCase();
    const umTargetNorm = (umTarget || "").toLowerCase().trim();

    // 0. Se umTarget = "pz": conta pezzi dalla descrizione (es. Pz.18×30Gr → 18 pz/pacco × qty)
    if (umTargetNorm === "pz") {
      // PRIMA controlla se la descrizione indica un pack multiplo (Pz.18x30Gr → 18 pz per unità)
      // Questo gestisce il caso UM fattura = "PZ" ma ogni "PZ" è un pacco da N pezzi
      const pzPerPacco = estraiPezziPerPacco(description);
      if (pzPerPacco !== null && pzPerPacco > 1) {
        return { qta: Number(quantity) * pzPerPacco, um: "pz" };
      }
      // UM già in pz/nr e nessun pack indicator → quantità grezza
      if (["PZ", "N.", "NR", "N", "PEZZI"].includes(umUp)) {
        return { qta: Number(quantity), um: "pz" };
      }
      // Fallback: usa qty grezza come pz
      return { qta: Number(quantity), um: "pz" };
    }

    // 0b. Auto-detect pz: UM pacco (BUS/CF/ecc.) + descrizione con pezzi espliciti (Pz.N, NCF*MPZ)
    // Solo quando umTarget è null (ingrediente non ancora associato → nessuna preferenza utente)
    // GUARD: attiva solo se "PZ" è esplicitamente nella descrizione (evita "6x400G" pelati/caffè)
    const UM_PACK = new Set(["BUS","BUST","BUSTA","CF","COL","COLLO","FUSTO","SAC","SACC","SCAT","CONF","CART"]);
    if (!umTargetNorm && UM_PACK.has(umUp) && (description || "").toUpperCase().includes("PZ")) {
      const pzAuto = estraiPezziPerPacco(description);
      if (pzAuto !== null) {
        const totPz = Number(quantity) * pzAuto;
        return { qta: totPz, um: "pz" };
      }
    }

    // 1. UM fattura nativa (peso/volume)
    if (umUp === "KG") {
      // Caso speciale: UM=KG ma la descrizione riporta il peso unitario (es. "Kg.2,275")
      // → qty è il numero di confezioni, non il peso totale
      const qtyVal = Number(quantity);
      if (Number.isInteger(qtyVal) && qtyVal > 1 && qtyVal <= 20) {
        const mKg = descUp.match(/\bKG\.?\s*(\d+(?:[,.]\d+)?)\b/);
        if (mKg) {
          const unitKg = parseFloat(mKg[1].replace(",", "."));
          if (Math.abs(unitKg - qtyVal) > 0.05 && unitKg >= 0.1 && unitKg <= 50) {
            return { qta: qtyVal * unitKg, um: "kg" };
          }
        }
      }
      return { qta: Number(quantity), um: "kg" };
    }
    if (umUp === "G")  return { qta: Number(quantity) / 1000, um: "kg" };
    if (umUp === "L" || umUp === "LT") return { qta: Number(quantity), um: "lt" };
    if (umUp === "ML") return { qta: Number(quantity) / 1000, um: "lt" };
    if (umUp === "CL") return { qta: Number(quantity) / 100, um: "lt" };

    // 2. Pattern NxN,N (KG|GR|G|L|LT|ML|CL) — es. "6x500G", "4x3,78L", "6/1.5LT", "24X33CL", "Pz.18×30Gr"
    const formatoMatch = descUp.match(/(\d+)\s*[xX×/÷]\s*(\d+(?:[,.]\d+)?)\s*(KG|GR|G|ML|CL|LT|L)\b/);
    if (formatoMatch) {
      const pezzi = parseInt(formatoMatch[1]);
      const peso = parseFloat(formatoMatch[2].replace(",", "."));
      const unit = formatoMatch[3];
      let pezzoNorm = peso;
      let umNorm = "kg";
      if (unit === "G" || unit === "GR") { pezzoNorm = peso / 1000; umNorm = "kg"; }
      else if (unit === "ML") { pezzoNorm = peso / 1000; umNorm = "lt"; }
      else if (unit === "CL") { pezzoNorm = peso / 100; umNorm = "lt"; }
      else if (unit === "L" || unit === "LT") { pezzoNorm = peso; umNorm = "lt"; }
      else if (unit === "KG") { pezzoNorm = peso; umNorm = "kg"; }
      return { qta: Number(quantity) * pezzi * pezzoNorm, um: umNorm };
    }

    // 2b. Pattern "GR.WxN" / "G.WxN" → W grammi PER PEZZO × N pezzi (unità PRIMA, nessuna unità finale)
    //     es. "Churros Gr.18x245" → 18 g × 245 pz = 4410 g = 4.41 kg per confezione
    const grWxN = descUp.match(/\b(?:GR|G)\.?\s*(\d+)\s*[xX×*]\s*(\d+)\b(?!\s*(?:KG|GR|G|ML|CL|LT|L|PZ))/);
    if (grWxN) {
      const grammiPezzo = parseInt(grWxN[1]);
      const numPezzi = parseInt(grWxN[2]);
      if (grammiPezzo >= 1 && grammiPezzo <= 2000 && numPezzi >= 2 && numPezzi <= 5000) {
        const kgConf = (grammiPezzo * numPezzi) / 1000;
        return { qta: Number(quantity) * kgConf, um: "kg" };
      }
    }
    // 3. Pattern "numero+unità" semplice nella descrizione (es. "1kg", "750 ml", "875GR", "33CL", "Kg.2,275")
    const singleMatch = descUp.match(/(\d+(?:[,.]\d+)?)\s*(KG|GR|G|ML|CL|LT|L)\b/);
    if (singleMatch) {
      const value = parseFloat(singleMatch[1].replace(",", "."));
      const unit = singleMatch[2];
      const plausibile = (
        ((unit === "G" || unit === "GR") && value >= 5 && value <= 5000) ||
        (unit === "ML" && value >= 50 && value <= 5000) ||
        (unit === "CL" && value >= 5 && value <= 500) ||
        (unit === "KG" && value >= 0.1 && value <= 100) ||
        ((unit === "L" || unit === "LT") && value >= 0.1 && value <= 200)
      );
      if (plausibile) {
        let pezzoNorm = value;
        let umNorm = "kg";
        if (unit === "G" || unit === "GR") { pezzoNorm = value / 1000; umNorm = "kg"; }
        else if (unit === "ML") { pezzoNorm = value / 1000; umNorm = "lt"; }
        else if (unit === "CL") { pezzoNorm = value / 100; umNorm = "lt"; }
        else if (unit === "L" || unit === "LT") { pezzoNorm = value; umNorm = "lt"; }
        else if (unit === "KG") { pezzoNorm = value; umNorm = "kg"; }
        return { qta: Number(quantity) * pezzoNorm, um: umNorm };
      }
    }

    // 3b. Pattern italiano: unità PRIMA del numero — "Kg.2,275", "Gr.875", "Lt.500", "Cl.70"
    // Mirror del Pattern D in confezionamento_parser.py (extract_pack_info)
    const italianMatch = descUp.match(/\b(KG|GR|G|ML|CL|LT|L)\.?\s*(\d+(?:[,.]\d+)?)\b/);
    if (italianMatch) {
      const italianValue = parseFloat(italianMatch[2].replace(",", "."));
      const italianUnit = italianMatch[1];
      const italianPlausibile = (
        ((italianUnit === "G" || italianUnit === "GR") && italianValue >= 5 && italianValue <= 5000) ||
        (italianUnit === "ML" && italianValue >= 50 && italianValue <= 5000) ||
        (italianUnit === "CL" && italianValue >= 5 && italianValue <= 500) ||
        (italianUnit === "KG" && italianValue >= 0.1 && italianValue <= 100) ||
        ((italianUnit === "L" || italianUnit === "LT") && italianValue >= 0.1 && italianValue <= 200)
      );
      if (italianPlausibile) {
        let pezzoNormI = italianValue;
        let umNormI = "kg";
        if (italianUnit === "G" || italianUnit === "GR") { pezzoNormI = italianValue / 1000; umNormI = "kg"; }
        else if (italianUnit === "ML") { pezzoNormI = italianValue / 1000; umNormI = "lt"; }
        else if (italianUnit === "CL") { pezzoNormI = italianValue / 100; umNormI = "lt"; }
        else if (italianUnit === "L" || italianUnit === "LT") { pezzoNormI = italianValue; umNormI = "lt"; }
        else if (italianUnit === "KG") { pezzoNormI = italianValue; umNormI = "kg"; }
        return { qta: Number(quantity) * pezzoNormI, um: umNormI };
      }
    }

    // 4. Pattern italiano naturale: "peso totale N chilogramm*" / "peso N kg"
    // Gestisce descrizioni come "Peso totale 3 chilogrammi" (es. Dulce de Leche 3 kg/set × 4 pz)
    const pesoTotaleMatch = descUp.match(
      /\bPESO\s+(?:TOTALE|NETTO|LORDO)?\s*:?\s*(\d+(?:[,.]\d+)?)\s*(CHILOGRAMM[IO]|KILO(?:GRAMM[IO])?|GRAMM[IO]|GRAMMI|KG|G\b|GR\b)/
    );
    if (pesoTotaleMatch) {
      const val = parseFloat(pesoTotaleMatch[1].replace(",", "."));
      const un = pesoTotaleMatch[2];
      const pesoNorm = (un.startsWith("GRAMM") || un === "GR" || un === "G") ? val / 1000 : val;
      if (pesoNorm > 0 && pesoNorm <= 2000) {
        return { qta: Number(quantity) * pesoNorm, um: "kg" };
      }
    }

    // 4b. Pattern "N confezioni/pezzi/buste/sacchi da X kg/chilogramm*/gramm*"
    // Gestisce: "3 confezioni da 1 chilogrammo", "6 pezzi da 400 grammi", "12 buste da 250g" ecc.
    const confDaMatch = descUp.match(
      /(\d+)\s*(?:CONFEZIONI?|PEZZI?|BUSTE?|SACCHI?|PACCHI?|BOTTIGLI[AE]|FLACONI?|VASETTI?|BARATTOLI?|VASCHETTE?)\s+DA\s+(\d+(?:[,.]\d+)?)\s*(CHILOGRAMM[IO]|KILO(?:GRAMM[IO])?|GRAMM[IO]|GRAMMI|KG\b|GR\b|G\b|LITRI?|LT\b|L\b)/
    );
    if (confDaMatch) {
      const nConf = parseInt(confDaMatch[1]);
      const valConf = parseFloat(confDaMatch[2].replace(",", "."));
      const unConf = confDaMatch[3];
      let pesoUnitConf, umConf;
      if (unConf.startsWith("GRAMM") || unConf === "GR" || unConf === "G") {
        pesoUnitConf = valConf / 1000; umConf = "kg";
      } else if (unConf.startsWith("LITRI") || unConf === "LT" || unConf === "L") {
        pesoUnitConf = valConf; umConf = "lt";
      } else {
        pesoUnitConf = valConf; umConf = "kg";
      }
      const pesoPerUnita = nConf * pesoUnitConf;
      if (pesoPerUnita > 0 && pesoPerUnita <= 2000) {
        return { qta: Number(quantity) * pesoPerUnita, um: umConf };
      }
    }

    // 4c. Pattern "N chilogramm[i/o]" o "N grammi" scritto in italiano (parola intera)
    // Solo quando NON preceduto da "DA " (evita "da 1 chilogrammo" già gestito sopra)
    const italianWordMatch = descUp.match(/(?<!DA\s{0,3})(?<![A-Z])(\d+(?:[,.]\d+)?)\s*(CHILOGRAMM[IO]|KILO(?:GRAMM[IO])?|GRAMM[IO]|GRAMMI)\b/);
    if (italianWordMatch) {
      const valW = parseFloat(italianWordMatch[1].replace(",", "."));
      const unW = italianWordMatch[2];
      const pesoW = (unW.startsWith("GRAMM") || unW === "GRAMMI") ? valW / 1000 : valW;
      if (pesoW > 0 && pesoW <= 2000) {
        return { qta: Number(quantity) * pesoW, um: "kg" };
      }
    }

    // 5. Fallback: quantità grezza, unità pz
    return { qta: Number(quantity), um: "pz" };
  }

  // Whitelist parole-chiave per il suggerimento automatico dell'UM di gestione.
  // Mirror della logica backend in confezionamento_parser.suggerisci_um_default.
  const BEVANDE_KW = new Set([
    "ACQUA","VINO","VINI","ROSSO","BIANCO","ROSATO","PROSECCO","SPUMANTE","CHAMPAGNE",
    "BIRRA","BIRRE","LAGER","PILS","STOUT","WEISS","IPA",
    "AMARO","AMARI","LIQUORE","LIQUORI","GRAPPA","GRAPPE",
    "GIN","VODKA","RUM","WHISKY","WHISKEY","TEQUILA","BRANDY","COGNAC",
    "VERMOUTH","APERITIVO","BITTER","CAMPARI","APEROL","MARTINI",
    "LIMONCELLO","SAMBUCA","MIRTO","FERNET","CYNAR","MENTA",
    "SUCCO","SUCCHI","BIBITA","BIBITE","COLA","ARANCIATA","CHINOTTO",
    "GASSOSA","TONICA","TONIC","SODA","SPRITE","FANTA",
    "SCIROPPO","SCIROPPI","FRAPPE",
  ]);
  const LIQUIDI_SFUSI_KW = new Set([
    "LATTE","PANNA","YOGURT","OLIO","ACETO","BALSAMICO","VINAIGRETTE",
    "BRODO","BRODI","SALSA","SUGO","PASSATA","POLPA","PELATI",
    "MAIONESE","SENAPE","KETCHUP",
  ]);

  function suggestUmDefault(descrizione, umFattura) {
    const umUp = (umFattura || "").toUpperCase().trim();
    if (!descrizione) {
      if (umUp === "KG" || umUp === "G") return "kg";
      if (["LT","L","ML","CL"].includes(umUp)) return "lt";
      return "kg";
    }
    const descUp = descrizione.toUpperCase();
    const tokens = new Set((descUp.match(/[A-ZÀ-Ý]{3,}/g) || []));
    // 1. bevande → pz
    for (const t of tokens) if (BEVANDE_KW.has(t)) return "pz";
    // 2. pattern multi-pack volume → pz (bottiglia)
    if (/\b\d+\s*[xX×/÷]\s*\d+(?:[,.]\d+)?\s*(ML|CL|LT|L)\b/.test(descUp)) return "pz";
    // 3. liquidi sfusi → lt
    for (const t of tokens) if (LIQUIDI_SFUSI_KW.has(t)) return "lt";
    // 4. fallback UM fattura
    if (umUp === "KG" || umUp === "G") return "kg";
    if (["LT","L","ML","CL"].includes(umUp)) return "lt";
    return "kg";
  }

  // Wrapper legacy (kg-only) per i call site esistenti
  function calcolaQuantitaKg(quantity, um, description, umTarget = null, codice = null) {
    return calcolaQuantitaUm(quantity, um, description, umTarget, codice).qta;
  };

  // Apri dialog associazione
  const apriAssociaDialog = (riga) => {
    setRigaCorrente(riga);
    setSearchIngrediente("");
    setTipoProdottoDialog(riga.tipo_prodotto === 'materiale' ? 'materiale' : 'food');
    setAssociaDialogOpen(true);
  };

  // Dialog Cambia UM ingrediente master (durante revisione SDI)
  const [umDialogIngrediente, setUmDialogIngrediente] = useState(null); // { id, nome, unita_misura }
  const [umDialogNewUm, setUmDialogNewUm] = useState("kg");
  const [umDialogSaving, setUmDialogSaving] = useState(false);

  // Dialog Override calcolo riga (correzione manuale prima dell'import)
  const [rigaOverrideDialog, setRigaOverrideDialog] = useState(null); // riga
  const [overrideQta, setOverrideQta] = useState("");
  const [overridePrezzo, setOverridePrezzo] = useState("");
  const [overrideUm, setOverrideUm] = useState("kg");

  const apriRigaOverrideDialog = (riga) => {
    const ingMaster = ingredienti.find(i => i.id === riga.ingrediente_id);
    const um = (riga.um_override || ingMaster?.unita_misura || "kg").toLowerCase();
    setRigaOverrideDialog(riga);
    setOverrideQta(String(riga.qta_override ?? riga.quantita_kg ?? 0));
    setOverridePrezzo(String(riga.prezzo_override ?? riga.prezzo_kg ?? 0));
    setOverrideUm(um);
  };

  const salvaRigaOverride = async () => {
    if (!rigaOverrideDialog) return;
    const qta = parseFloat(String(overrideQta).replace(",", "."));
    const prezzo = parseFloat(String(overridePrezzo).replace(",", "."));
    if (!qta || qta <= 0 || !prezzo || prezzo <= 0) {
      toast.error("Inserisci quantità e prezzo unitario validi (> 0)");
      return;
    }
    const riga = rigaOverrideDialog;
    setRighe(prev => prev.map(r =>
      r.id === riga.id
        ? {
            ...r,
            quantita_kg: qta,        // display
            prezzo_kg: prezzo,       // display
            qta_override: qta,
            prezzo_override: prezzo,
            um_override: overrideUm,
            override: true,
          }
        : r
    ));

    // APPRENDIMENTO: memorizza la conversione (qta target per 1 unità di fattura)
    // così alle prossime fatture dello stesso prodotto verrà applicata in automatico.
    const qtaFattura = Number(riga.quantity) || 0;
    if (qtaFattura > 0 && fornitoreSelezionato?.id) {
      const fattore = qta / qtaFattura;
      const codiceKey = (riga.codice_articolo || "").trim().toUpperCase();
      const descKey = (riga.description || "").trim().toUpperCase().replace(/\s+/g, " ");
      if (codiceKey || descKey) {
        const umNorm = normUmFattura(riga.unit_of_measure);
        const entry = { fattore, um_target: overrideUm, um_fattura: umNorm };
        conversioniRef.current = {
          byCodice: { ...conversioniRef.current.byCodice },
          byDesc: { ...conversioniRef.current.byDesc },
        };
        // Aggiorna il gruppo per UdM e (se abbiamo sia PZ che CT) ricava i pezzi per cartone.
        const upsertGroup = (map, key) => {
          let g = map[key] || {};
          if (typeof g.fattore === "number") {
            // migra la vecchia voce piatta nel gruppo per UdM
            const oldUm = normUmFattura(g.um_fattura);
            g = { [oldUm]: { fattore: g.fattore, um_target: g.um_target, um_fattura: oldUm } };
          } else {
            g = { ...g };
          }
          g[umNorm] = entry;
          if (g.PZ?.fattore > 0 && g.CT?.fattore > 0) {
            g.pz_per_ct = Math.round((g.CT.fattore / g.PZ.fattore) * 1000) / 1000;
          }
          map[key] = g;
        };
        if (codiceKey) upsertGroup(conversioniRef.current.byCodice, codiceKey);
        if (descKey) upsertGroup(conversioniRef.current.byDesc, descKey);
        try {
          await axios.post(`${API}/sdi/conversioni-apprese`, {
            fornitore_id: fornitoreSelezionato.id,
            codice_articolo: riga.codice_articolo || null,
            descrizione_fattura: riga.description || null,
            um_fattura: riga.unit_of_measure || null,
            fattore,
            um_target: overrideUm,
          }, { headers: getAuthHeaders() });
          toast.success("Correzione memorizzata: la applicherò in automatico la prossima volta");
        } catch {
          toast.success("Calcolo riga modificato manualmente");
        }
      } else {
        toast.success("Calcolo riga modificato manualmente");
      }
    } else {
      toast.success("Calcolo riga modificato manualmente");
    }
    setRigaOverrideDialog(null);
  };

  const rimuoviOverride = (rigaId) => {
    setRighe(prev => prev.map(r =>
      r.id === rigaId
        ? (() => {
            const { qta_override, prezzo_override, um_override, override, ...rest } = r;
            return rest;
          })()
        : r
    ));
    toast.info("Override rimosso, calcolo automatico ripristinato");
  };

  const apriCambiaUmDialog = (ingrediente_id) => {
    const ing = ingredienti.find(i => i.id === ingrediente_id);
    if (!ing) return;
    setUmDialogIngrediente(ing);
    setUmDialogNewUm(ing.unita_misura || "kg");
  };

  const salvaCambioUm = async () => {
    if (!umDialogIngrediente || !umDialogNewUm) return;
    setUmDialogSaving(true);
    try {
      await axios.put(
        `${API}/ingredienti/${umDialogIngrediente.id}`,
        { ...umDialogIngrediente, unita_misura: umDialogNewUm },
        { headers: getAuthHeaders() }
      );
      // Aggiorna lo state locale degli ingredienti
      setIngredienti(prev => prev.map(i => i.id === umDialogIngrediente.id ? { ...i, unita_misura: umDialogNewUm } : i));
      // Ricalcola tutte le righe associate a questo ingrediente
      setRighe(prev => prev.map(r => {
        if (r.ingrediente_id !== umDialogIngrediente.id || !r.associato) return r;
        const { qta, um } = calcolaQuantitaUm(r.quantity, r.unit_of_measure, r.description);
        const _ = um; // l'UM mostrata sarà comunque quella master ricalcolata dal backend all'import
        // Per coerenza visiva, usa l'UM master appena salvata come riferimento di display
        const quantitaUmTarget = umDialogNewUm === "pz"
          ? (() => {
              // Match pattern "PZ.NxM" o "NxM <unit>" → totale pezzi = N × quantity
              const m = (r.description || "").toUpperCase().match(/\b(?:PZ\.?|)\s*(\d+)\s*[xX×/÷]\s*\d/);
              const pezziPerPacco = m ? parseInt(m[1]) : 1;
              return r.quantity * pezziPerPacco;
            })()
          : qta;
        const prezzo = quantitaUmTarget > 0 ? r.total_price / quantitaUmTarget : 0;
        return {
          ...r,
          quantita_kg: quantitaUmTarget,
          prezzo_kg: prezzo,
          um_display: umDialogNewUm,
        };
      }));
      toast.success(`UM aggiornata a "${umDialogNewUm}" per ${umDialogIngrediente.nome}`);
      setUmDialogIngrediente(null);
    } catch (e) {
      toast.error("Errore aggiornamento UM ingrediente");
    } finally {
      setUmDialogSaving(false);
    }
  };

  // Associa ingrediente manualmente
  const associaIngrediente = (ingredienteId) => {

    if (!rigaCorrente) return;
    
    const ingrediente = ingredienti.find(i => i.id === ingredienteId);
    if (!ingrediente) return;
    
    const quantitaKg = calcolaQuantitaKg(
      rigaCorrente.quantity, 
      rigaCorrente.unit_of_measure, 
      rigaCorrente.description,
      ingrediente.unita_misura,
      rigaCorrente.codice_articolo
    );
    const prezzoKg = quantitaKg > 0 ? rigaCorrente.total_price / quantitaKg : rigaCorrente.unit_price;
    
    setRighe(prev => prev.map(r => 
      r.id === rigaCorrente.id 
        ? {
            ...r,
            ingrediente_id: ingrediente.id,
            ingrediente_nome: ingrediente.nome,
            quantita_kg: quantitaKg,
            prezzo_kg: prezzoKg,
            associato: true,
            match_type: 'manuale',
            match_score: 100
          }
        : r
    ));
    
    setAssociaDialogOpen(false);
    toast.success(`Associato "${rigaCorrente.description}" → "${ingrediente.nome}"`);
  };

  // Apri dialog nuovo ingrediente
  const apriNuovoIngredienteDialog = () => {
    const umSuggerita = suggestUmDefault(
      rigaCorrente?.description,
      rigaCorrente?.unit_of_measure
    );
    setNuovoIngrediente({
      nome: rigaCorrente?.description || "",
      categoria_id: "",
      unita_misura: umSuggerita || "kg"
    });
    setAssociaDialogOpen(false);
    setNuovoIngredienteDialogOpen(true);
  };

  // Crea nuovo ingrediente
  const creaNuovoIngrediente = async () => {
    if (!nuovoIngrediente.nome.trim()) {
      toast.error("Inserisci un nome per l'ingrediente");
      return;
    }
    
    try {
      setCreatingIngrediente(true);
      
      const quantitaKg = calcolaQuantitaKg(
        rigaCorrente.quantity,
        rigaCorrente.unit_of_measure,
        rigaCorrente.description,
        nuovoIngrediente.unita_misura,
        rigaCorrente.codice_articolo
      );
      const prezzoKg = quantitaKg > 0 ? rigaCorrente.total_price / quantitaKg : rigaCorrente.unit_price;
      
      const response = await axios.post(`${API}/sdi/ingrediente-quick`, {
        locale_id: localeCorrente.id,
        nome: nuovoIngrediente.nome.trim(),
        categoria_id: nuovoIngrediente.categoria_id || null,
        unita_misura: nuovoIngrediente.unita_misura,
        fornitore_id: fornitoreSelezionato?.id,
        fornitore_nome: fornitoreSelezionato?.nome,
        prezzo_kg: prezzoKg,
        codice_fornitore: rigaCorrente.codice_articolo,
        descrizione_fattura: rigaCorrente.description,
        data_fattura: dataFattura || null,
        iva_percentuale: rigaCorrente.vat_rate ?? null,
      }, { headers: getAuthHeaders() });
      
      if (response.data.success) {
        const newIng = response.data.ingrediente;
        
        // Aggiungi ai locali
        setIngredienti(prev => [...prev, newIng]);
        
        // Associa alla riga
        setRighe(prev => prev.map(r => 
          r.id === rigaCorrente.id 
            ? {
                ...r,
                ingrediente_id: newIng.id,
                ingrediente_nome: newIng.nome,
                quantita_kg: quantitaKg,
                prezzo_kg: prezzoKg,
                associato: true,
                match_type: 'nuovo',
                match_score: 100
              }
            : r
        ));
        
        setNuovoIngredienteDialogOpen(false);
        toast.success(`Ingrediente "${newIng.nome}" creato e associato`);
      }
    } catch (error) {
      console.error("Errore creazione ingrediente:", error);
      toast.error("Errore durante la creazione dell'ingrediente");
    } finally {
      setCreatingIngrediente(false);
    }
  };

  // Associa materiale non-food
  const associaMateriale = (materialeId) => {
    if (!rigaCorrente) return;
    const mat = materiali.find(m => m.id === materialeId);
    if (!mat) return;
    const qta = rigaCorrente.quantity || 0;
    const totale = rigaCorrente.total_price || 0;
    const prezzoUnit = qta > 0 ? totale / qta : rigaCorrente.unit_price || 0;
    setRighe(prev => prev.map(r =>
      r.id === rigaCorrente.id
        ? {
            ...r,
            tipo_prodotto: "materiale",
            materiale_id: mat.id,
            materiale_nome: mat.nome,
            ingrediente_id: null,
            ingrediente_nome: null,
            quantita_kg: qta,
            prezzo_kg: prezzoUnit,
            associato: true,
            match_type: 'manuale',
            match_score: 100
          }
        : r
    ));
    setAssociaDialogOpen(false);
    toast.success(`Associato "${rigaCorrente.description}" → "${mat.nome}"`);
  };

  // Apri dialog nuovo materiale non-food
  const apriNuovoMaterialeDialog = () => {
    setNuovoMateriale({
      nome: rigaCorrente?.description || "",
      categoria_id: categorieMateriali[0]?.id || "",
      unita_misura: "pz",
      tipo: "packaging"
    });
    setAssociaDialogOpen(false);
    setNuovoMaterialeDialogOpen(true);
  };

  // Crea nuovo materiale non-food
  const creaNuovoMateriale = async () => {
    if (!nuovoMateriale.nome.trim()) {
      toast.error("Inserisci un nome per il materiale");
      return;
    }
    if (!nuovoMateriale.categoria_id) {
      toast.error("Seleziona una categoria");
      return;
    }
    try {
      setCreatingMateriale(true);
      const qta = rigaCorrente?.quantity || 0;
      const totale = rigaCorrente?.total_price || 0;
      const prezzoUnit = qta > 0 ? totale / qta : rigaCorrente?.unit_price || 0;
      const response = await axios.post(`${API}/materiali`, {
        locale_id: localeCorrente.id,
        nome: nuovoMateriale.nome.trim(),
        categoria_id: nuovoMateriale.categoria_id,
        tipo: nuovoMateriale.tipo,
        unita_misura: nuovoMateriale.unita_misura,
        fornitore_id: fornitoreSelezionato?.id || null,
        prezzo_unitario: prezzoUnit || null,
        iva_percentuale: rigaCorrente?.vat_rate ?? null,
      }, { headers: getAuthHeaders() });
      const newMat = response.data;
      setMateriali(prev => [...prev, newMat]);
      setRighe(prev => prev.map(r =>
        r.id === rigaCorrente?.id
          ? {
              ...r,
              tipo_prodotto: "materiale",
              materiale_id: newMat.id,
              materiale_nome: newMat.nome,
              ingrediente_id: null,
              ingrediente_nome: null,
              quantita_kg: qta,
              prezzo_kg: prezzoUnit,
              associato: true,
              match_type: 'nuovo',
              match_score: 100
            }
          : r
      ));
      setNuovoMaterialeDialogOpen(false);
      toast.success(`Materiale "${newMat.nome}" creato e associato`);
    } catch (error) {
      console.error("Errore creazione materiale:", error);
      const msg = error.response?.data?.detail || "Errore durante la creazione del materiale";
      toast.error(msg);
    } finally {
      setCreatingMateriale(false);
    }
  };

  // Rimuovi associazione
  const rimuoviAssociazione = (rigaId) => {
    setRighe(prev => prev.map(r => 
      r.id === rigaId 
        ? {
            ...r,
            tipo_prodotto: null,
            ingrediente_id: null,
            ingrediente_nome: null,
            materiale_id: null,
            materiale_nome: null,
            quantita_kg: null,
            prezzo_kg: null,
            associato: false,
            match_type: null,
            match_score: null
          }
        : r
    ));
  };

  // Toggle esclusione riga
  const toggleEscludi = (rigaId) => {
    setRighe(prev => prev.map(r => 
      r.id === rigaId ? { ...r, escluso: !r.escluso } : r
    ));
  };

  // Apri modal conferma (invece di importare direttamente)
  const apriConfermaImport = () => {
    if (!fornitoreSelezionato) {
      setFornitoreMissingOpen(true);
      return;
    }
    setConfirmImportOpen(true);
  };

  // ── Confronto col prezzo ATTUALE del prodotto (rete di sicurezza errori quantità) ──
  const SOGLIA_SCOSTAMENTO = 0.20; // ±20%

  const getPrezzoAttuale = (riga) => {
    if (riga.tipo_prodotto === 'materiale') {
      const m = materiali.find(x => x.id === riga.materiale_id);
      const p = m?.prezzo_unitario ?? m?.prezzo_migliore;
      return p && p > 0 ? p : null;
    }
    const ing = ingredienti.find(x => x.id === riga.ingrediente_id);
    const p = ing?.prezzo_attuale;
    return p && p > 0 ? p : null;
  };

  const getScostamento = (riga) => {
    const attuale = getPrezzoAttuale(riga);
    if (attuale == null || !riga.prezzo_kg) return null;
    return (riga.prezzo_kg - attuale) / attuale;
  };

  const isScostamentoSospetto = (riga) => {
    const s = getScostamento(riga);
    return s != null && Math.abs(s) > SOGLIA_SCOSTAMENTO;
  };

  // Righe "sicure" per l'import automatico: match certo (codice articolo / descrizione memorizzata) + prezzo coerente
  const isRigaSicura = (riga) => (
    !riga.escluso && !riga.importato && riga.associato &&
    (riga.ingrediente_id || riga.materiale_id) &&
    ['codice', 'descrizione'].includes(riga.match_type) &&
    !isScostamentoSospetto(riga)
  );

  // Costruisce il payload di una riga per l'import
  const buildRigaPayload = (r) => {
    const _conv = conversioniRef.current || { byCodice: {}, byDesc: {} };
    const _codiceKey = (r.codice_articolo || "").trim().toUpperCase();
    const _descKey = (r.description || "").trim().toUpperCase().replace(/\s+/g, " ");
    const _group = (_codiceKey && _conv.byCodice[_codiceKey]) || (_descKey && _conv.byDesc[_descKey]);
    const _learned = resolveLearned(_group, r.unit_of_measure);
    const _isOverride = !!r.override || !!_learned;
    return {
      tipo_prodotto: r.tipo_prodotto || "food",
      ingrediente_id: r.ingrediente_id || null,
      ingrediente_nome: r.ingrediente_nome || null,
      materiale_id: r.materiale_id || null,
      materiale_nome: r.materiale_nome || null,
      descrizione_fattura: r.description,
      quantita_kg: r.quantita_kg,
      prezzo_kg: r.prezzo_kg,
      totale: r.total_price,
      um_fattura: r.unit_of_measure,
      quantita_fattura: r.quantity,
      codice_fornitore: r.codice_articolo,
      override: _isOverride,
      qta_override: r.qta_override ?? (_learned ? r.quantita_kg : null),
      prezzo_override: r.prezzo_override ?? (_learned ? r.prezzo_kg : null),
      um_override: r.um_override ?? (_learned ? (_learned.um_target || null) : null),
      aliquota: r.vat_rate ?? null,
      escluso: false
    };
  };

  // Marca la fattura come importata nella cache localStorage
  const markFatturaImportataInCache = () => {
    try {
      const rawFattura = sessionStorage.getItem("sdi_fattura_data");
      const fatturaImportata = rawFattura ? JSON.parse(rawFattura) : null;
      const localeId = localeCorrente?.id || "default";
      const cacheKey = `sdi_fatture_cache_${localeId}`;
      const rawCache = localStorage.getItem(cacheKey);
      if (rawCache && fatturaImportata?.id) {
        const cached = JSON.parse(rawCache);
        cached.matchFornitori = cached.matchFornitori || {};
        cached.matchFornitori[fatturaImportata.id] = {
          ...(cached.matchFornitori[fatturaImportata.id] || {}),
          imported: true,
        };
        localStorage.setItem(cacheKey, JSON.stringify(cached));
      }
    } catch (e) {
      console.warn("Cache SDI: impossibile aggiornare dopo import", e);
    }
  };

  // Import generico di un sottoinsieme di righe (riusato da import manuale e automatico)
  // partial=true → totale_fattura = somma delle sole righe importate (evita doppio conteggio negli import a fasi)
  const eseguiImport = async (righeDaImportare, { navigateOnSuccess = true, markImported = false, partial = false } = {}) => {
    if (!righeDaImportare || righeDaImportare.length === 0) return false;
    try {
      setSaving(true);
      setConfirmImportOpen(false);

      const totaleRighe = righeDaImportare.reduce((s, r) => s + (parseFloat(r.total_price) || 0), 0);
      const requestData = {
        locale_id: localeCorrente.id,
        fornitore_id: fornitoreSelezionato?.id || null,
        fornitore_nome: fornitoreSelezionato?.nome || fornitoreSelezionato?.ragione_sociale || fattura?.sender?.name || "Fornitore SDI",
        data_fattura: dataFattura,
        numero_fattura: fattura?.document_number || "",
        totale_fattura: partial ? totaleRighe : (fattura?.total_amount || 0),
        sdi_id: fattura?.sdi_id || null,
        righe: righeDaImportare.map(buildRigaPayload),
        crea_uscita: false,
        aggiorna_iva: aggiornaIvaImport
      };

      const response = await axios.post(`${API}/sdi/import-fattura`, requestData, { headers: getAuthHeaders() });

      if (response.data.success) {
        if (markImported) {
          const idsImport = new Set(righeDaImportare.map(r => r.id));
          setRighe(prev => prev.map(r => idsImport.has(r.id) ? { ...r, importato: true, escluso: true } : r));
        }
        markFatturaImportataInCache();
        let msg = `Importati ${response.data.prodotti_importati} prodotti`;
        if (response.data.codici_salvati > 0) msg += ` (${response.data.codici_salvati} codici salvati)`;
        toast.success(msg);
        if (navigateOnSuccess) {
          sessionStorage.removeItem("sdi_fattura_data");
          navigate("/acquisti/import-sdi");
        }
        return true;
      }
    } catch (error) {
      console.error("Errore salvataggio fattura:", error);
      console.error("Response data:", error.response?.data);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map(d => d.msg || JSON.stringify(d)).join("; ")
          : error.message || "Errore durante l'importazione della fattura";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
    return false;
  };

  // Import manuale (righe rimanenti associate e non escluse)
  const salvaFattura = async () => {
    const righeValide = righe.filter(r => !r.escluso && !r.importato && r.associato && (r.ingrediente_id || r.materiale_id));
    await eseguiImport(righeValide, { navigateOnSuccess: true, partial: autoImportEseguito });
  };

  // Import automatico dei soli match riconosciuti in sicurezza (1 click)
  const importAutomatico = async () => {
    if (!fornitoreSelezionato) {
      setFornitoreMissingOpen(true);
      return;
    }
    const safe = righe.filter(isRigaSicura);
    if (safe.length === 0) {
      toast.info("Nessuna riga riconosciuta in sicurezza da importare automaticamente");
      return;
    }
    const idsSafe = new Set(safe.map(r => r.id));
    const ok = await eseguiImport(safe, { navigateOnSuccess: false, markImported: true, partial: true });
    if (ok) {
      setAutoImportEseguito(true);
      // Righe ancora da gestire: da associare o con prezzo/quantità da verificare (escluse quelle appena importate e le auto-escluse)
      const daVerificare = righe.filter(r => !r.escluso && !idsSafe.has(r.id)).length;
      if (daVerificare === 0) {
        sessionStorage.removeItem("sdi_fattura_data");
        navigate("/acquisti/import-sdi");
      } else {
        toast.info(`${daVerificare} ${daVerificare === 1 ? "riga rimasta" : "righe rimaste"} da verificare: associa o controlla prezzo/quantità, poi importale.`);
      }
    }
  };

  // Formatta euro
  const formatEuro = (val) => {
    if (val === null || val === undefined) return "—";
    return `€ ${Number(val).toFixed(2)}`;
  };

  // Filtra ingredienti per ricerca (difensivo: ignora item senza nome)
  const ingredientiFiltrati = ingredienti.filter(ing =>
    (ing?.nome || "").toLowerCase().includes(searchIngrediente.toLowerCase())
  );

  // Filtra materiali per ricerca (difensivo: ignora item senza nome)
  const materialiFiltrati = materiali.filter(m =>
    (m?.nome || "").toLowerCase().includes(searchIngrediente.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-gray-500">Caricamento fattura...</div>
      </div>
    );
  }

  if (!fattura) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Nessuna fattura da processare</p>
        <Button onClick={() => navigate("/acquisti/import-sdi")} className="mt-4">
          Torna all'Import
        </Button>
      </div>
    );
  }

  const righeAssociate = righe.filter(r => r.associato && !r.escluso && !r.importato).length;
  const righeNonAssociate = righe.filter(r => !r.associato && !r.escluso && !r.importato).length;
  const righeEscluse = righe.filter(r => r.escluso && !r.importato).length;
  const righeEscluseAuto = righe.filter(r => r.escluso && r.escluso_auto && !r.importato).length;
  const righeImportate = righe.filter(r => r.importato).length;
  const righeSicureCount = righe.filter(isRigaSicura).length;
  const nessunaRiga = righeAssociate === 0; // Nessun prodotto da importare

  return (
    <div className="space-y-6" data-testid="revisione-fattura-sdi-page">
      {/* Avviso fattura già importata */}
      {giaImportata && !autoImportEseguito && (
        <Card className="border-amber-300 bg-amber-50" data-testid="banner-gia-importata">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              <span className="font-semibold">Attenzione: questa fattura ({fattura.document_number}) risulta già importata.</span>{" "}
              Importandola di nuovo creeresti un doppione di carico e di aggiornamento prezzi. Procedi solo se sei sicuro.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/acquisti/import-sdi")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-6 h-6 text-indigo-500" />
              Revisione Fattura SDI
            </h1>
            <p className="text-sm text-gray-500">
              {fattura.sender?.name} - {fattura.document_number}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/acquisti/import-sdi")}
          >
            Annulla
          </Button>
          {righeSicureCount > 0 && (
            <Button
              onClick={importAutomatico}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="importa-automatico-btn"
              title="Importa subito i prodotti già riconosciuti con prezzo coerente; controllerai a mano solo quelli da verificare"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Importa automaticamente {righeSicureCount} riconosciuti
            </Button>
          )}
          <Button
            onClick={apriConfermaImport}
            disabled={saving || nessunaRiga}
            className="bg-indigo-500 hover:bg-indigo-600"
            data-testid="importa-fattura-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? "Importazione..." : nessunaRiga ? "Nessun prodotto da importare" : `Importa ${righeAssociate} prodotti`}
          </Button>
        </div>
      </div>

      {/* Banner sticky CTA: ricorda all'utente di cliccare Importa per salvare definitivamente */}
      {!nessunaRiga && !saving && (
        <div
          className="sticky top-0 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-amber-50 border-y border-amber-200 flex flex-col sm:flex-row sm:items-center gap-3 shadow-sm"
          data-testid="banner-clicca-importa"
        >
          <div className="flex items-start gap-2 flex-1">
            <span className="text-xl leading-none" aria-hidden>👇</span>
            <div className="text-sm text-amber-900">
              <strong>Hai associato {righeAssociate} {righeAssociate === 1 ? "ingrediente" : "ingredienti"}.</strong>{" "}
              Per registrare il <strong>carico</strong>, aggiornare i <strong>prezzi</strong> con la data fattura e popolare lo <strong>Storico acquisti</strong>, clicca il pulsante qui sotto.{" "}
              <span className="text-amber-700">Se esci adesso senza importare, le associazioni vanno perse.</span>
            </div>
          </div>
          <Button
            onClick={apriConfermaImport}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 shrink-0"
            data-testid="banner-importa-fattura-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            Importa {righeAssociate} {righeAssociate === 1 ? "prodotto" : "prodotti"}
          </Button>
        </div>
      )}

      {/* Info Fattura */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-indigo-50">
                <Building2 className="w-5 h-5 text-indigo-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-500">Fornitore</p>
                <p className="font-bold text-gray-900 truncate">{fattura.sender?.name}</p>
                <p className="text-xs text-gray-500">{fattura.sender?.vat_code}</p>
              </div>
            </div>
            {fornitoreSelezionato ? (
              <div className="mt-2 flex items-center justify-between">
                <Badge className="bg-green-100 text-green-700">
                  <Check className="w-3 h-3 mr-1" />
                  {fornitoreSelezionato.nome}
                </Badge>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="h-6 px-2 text-xs text-gray-500 hover:text-gray-700"
                  onClick={() => { setSearchFornitore(""); setFornitoreDialogOpen(true); }}
                >
                  Cambia
                </Button>
              </div>
            ) : (
              <Button 
                variant="outline" 
                className="mt-2 w-full justify-start text-muted-foreground"
                onClick={() => { setSearchFornitore(""); setFornitoreDialogOpen(true); }}
              >
                <Building2 className="w-4 h-4 mr-2" />
                Seleziona fornitore...
              </Button>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-green-50">
                <Euro className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Totale Fattura</p>
                <p className="text-2xl font-bold text-gray-900">{formatEuro(fattura.total_amount)}</p>
                <p className="text-xs text-gray-500">IVA: {formatEuro(fattura.total_vat)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-4">
            <Label className="text-sm text-gray-500">Data Fattura</Label>
            <DateInput value={dataFattura} onChange={(v) => setDataFattura(v)} />
            <div className="flex items-center gap-2 mt-2">
              <Checkbox 
                id="aggiorna-prezzo"
                checked={aggiornaPrezzo}
                onCheckedChange={setAggiornaPrezzo}
              />
              <Label htmlFor="aggiorna-prezzo" className="text-sm text-gray-600 cursor-pointer">
                Aggiorna prezzi ingredienti
              </Label>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Statistiche Associazione */}
      <div className="flex flex-wrap gap-3">
        <Badge className="bg-green-100 text-green-700 px-3 py-1">
          <Check className="w-3 h-3 mr-1" />
          {righeAssociate} associati
        </Badge>
        <Badge className="bg-yellow-100 text-yellow-700 px-3 py-1">
          <AlertTriangle className="w-3 h-3 mr-1" />
          {righeNonAssociate} da associare
        </Badge>
        {righeImportate > 0 && (
          <Badge className="bg-emerald-100 text-emerald-700 px-3 py-1" data-testid="badge-gia-importate">
            <Check className="w-3 h-3 mr-1" />
            {righeImportate} già importate
          </Badge>
        )}
        {righeEscluse > 0 && (
          <Badge className="bg-gray-100 text-gray-700 px-3 py-1">
            <X className="w-3 h-3 mr-1" />
            {righeEscluse} esclusi
          </Badge>
        )}
      </div>

      {/* Notifica di controllo dopo l'import automatico */}
      {autoImportEseguito && (righeNonAssociate + righeAssociate) > 0 && (
        <Card className="border-emerald-200 bg-emerald-50" data-testid="banner-da-verificare">
          <CardContent className="p-3 flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-emerald-900">
              <span className="font-semibold">Prodotti riconosciuti importati automaticamente.</span>{" "}
              Restano <strong>{righeNonAssociate + righeAssociate}</strong> righe da controllare: quelle <strong>da associare</strong> o con <strong>prezzo/quantità da verificare</strong> (scostamento oltre il {Math.round(SOGLIA_SCOSTAMENTO * 100)}% dal prezzo attuale).
              {" "}Sistemale e premi <strong>“Importa {righeAssociate} prodotti”</strong>.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Banner auto-esclusione spese accessorie */}
      {righeEscluseAuto > 0 && (
        <Card className="border-amber-200 bg-amber-50" data-testid="banner-spese-accessorie">
          <CardContent className="p-3 flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              <span className="font-semibold">
                {righeEscluseAuto} {righeEscluseAuto === 1 ? "riga esclusa automaticamente" : "righe escluse automaticamente"}
              </span>
              {" "}— Le spese accessorie (trasporto, imballo, CONAI, pallet, sconti…) non sono materie prime e quindi non vengono importate.
              {" "}
              <span className="text-amber-700">Puoi includerle manualmente cliccando sulla X di una riga se serve.</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista Righe */}
      <div className="space-y-3">
        {righe.filter(r => !r.importato).map((riga) => (
          <Card 
            key={riga.id} 
            className={`bg-white transition-all ${riga.escluso ? "opacity-50" : ""} ${riga.associato ? "border-l-4 border-l-green-500" : "border-l-4 border-l-yellow-500"}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                {/* Checkbox Esclusione */}
                <div className="flex flex-col items-center gap-1 pt-1">
                  <Checkbox 
                    checked={!riga.escluso}
                    onCheckedChange={() => toggleEscludi(riga.id)}
                  />
                  <span className="text-xs text-gray-400">
                    {riga.escluso ? "Escluso" : "Incluso"}
                  </span>
                </div>

                {/* Info Prodotto Fattura */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-gray-900">{riga.description}</p>
                        {riga.escluso_auto && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium" data-testid={`badge-auto-esclusa-${riga.id}`}>
                            <Info className="w-3 h-3" /> Auto-esclusa: {riga.motivo_esclusione_auto}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-1 text-sm text-gray-500">
                        {riga.codice_articolo && (
                          <span className="flex items-center gap-1 text-indigo-600">
                            <Hash className="w-3 h-3" />
                            {riga.codice_articolo}
                          </span>
                        )}
                        <span>Qtà: {riga.quantity} {riga.unit_of_measure || "pz"}</span>
                        <span>•</span>
                        <span>Prezzo: {formatEuro(riga.unit_price)}</span>
                        <span>•</span>
                        <span className="font-medium text-gray-700">Tot: {formatEuro(riga.total_price)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Associazione */}
                  {!riga.escluso && (
                    <div className="mt-3 pt-3 border-t">
                      {riga.associato ? (
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge
                              className={`group cursor-pointer transition-opacity hover:opacity-80 ${
                                riga.tipo_prodotto === 'materiale'
                                  ? 'bg-orange-100 text-orange-700'
                                  : riga.match_type === 'codice' ? 'bg-indigo-100 text-indigo-700'
                                  : riga.match_type === 'descrizione' ? 'bg-purple-100 text-purple-700'
                                  : 'bg-green-100 text-green-700'
                              }`}
                              onClick={() => apriAssociaDialog(riga)}
                              title="Clicca per cambiare il prodotto associato"
                              data-testid={`badge-prodotto-${riga.id}`}
                            >
                              {riga.tipo_prodotto === 'materiale' && <Package className="w-3 h-3 mr-1" />}
                              {riga.tipo_prodotto !== 'materiale' && riga.match_type === 'codice' && <Hash className="w-3 h-3 mr-1" />}
                              {riga.tipo_prodotto !== 'materiale' && riga.match_type === 'descrizione' && <Sparkles className="w-3 h-3 mr-1" />}
                              {riga.tipo_prodotto !== 'materiale' && riga.match_type === 'nome' && <Sparkles className="w-3 h-3 mr-1" />}
                              {riga.tipo_prodotto !== 'materiale' && riga.match_type === 'manuale' && <Link2 className="w-3 h-3 mr-1" />}
                              {riga.tipo_prodotto !== 'materiale' && riga.match_type === 'nuovo' && <Plus className="w-3 h-3 mr-1" />}
                              {riga.tipo_prodotto === 'materiale' ? riga.materiale_nome : riga.ingrediente_nome}
                              <Pencil className="w-2.5 h-2.5 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </Badge>
                            {/* X rimuovi associazione — vicino al nome prodotto */}
                            <button
                              type="button"
                              onClick={() => rimuoviAssociazione(riga.id)}
                              title="Rimuovi associazione prodotto"
                              className="flex-shrink-0 w-4 h-4 rounded-full bg-red-100 hover:bg-red-200 text-red-500 hover:text-red-700 flex items-center justify-center transition-colors"
                              data-testid={`btn-rimuovi-assoc-${riga.id}`}
                            >
                              <X className="w-2.5 h-2.5" />
                            </button>
                            {riga.tipo_prodotto === 'materiale' ? (
                              <span className="text-xs text-orange-600 font-medium">Materiale</span>
                            ) : (
                              <>
                                {(() => {
                                  const ingMaster = ingredienti.find(i => i.id === riga.ingrediente_id);
                                  const umMaster = (ingMaster?.unita_misura || "kg").toLowerCase();
                                  return (
                                    <button
                                      type="button"
                                      onClick={() => apriCambiaUmDialog(riga.ingrediente_id)}
                                      className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 transition-colors"
                                      title="Modifica unità di misura dell'ingrediente"
                                      data-testid={`btn-cambia-um-${riga.id}`}
                                    >
                                      <span className="font-mono uppercase">{umMaster}</span>
                                      <Pencil className="w-3 h-3" />
                                    </button>
                                  );
                                })()}
                                {riga.match_type === 'codice' && (
                                  <span className="text-xs text-indigo-500">Match per codice</span>
                                )}
                                {riga.match_type === 'descrizione' && (
                                  <span className="text-xs text-purple-500">Match memorizzato</span>
                                )}
                                {riga.match_type === 'nome' && riga.match_score < 100 && (
                                  <span className="text-xs text-gray-400">Match: {riga.match_score}%</span>
                                )}
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right text-sm">
                              {(() => {
                                const ingMaster = ingredienti.find(i => i.id === riga.ingrediente_id);
                                const umDisplay = (riga.um_override || ingMaster?.unita_misura || "kg").toLowerCase();
                                const attuale = getPrezzoAttuale(riga);
                                const scost = getScostamento(riga);
                                const sospetto = scost != null && Math.abs(scost) > SOGLIA_SCOSTAMENTO;
                                return (
                                  <div>
                                    <p className={`${riga.override ? 'text-amber-700 font-semibold' : 'text-gray-500'}`}>
                                      {riga.override && <span title="Calcolo modificato manualmente" className="mr-1">✏️</span>}
                                      {riga.quantita_kg?.toFixed(2)} {umDisplay} @ {formatEuro(riga.prezzo_kg)}/{umDisplay}
                                    </p>
                                    {attuale != null && (
                                      <p className="text-xs text-gray-400" data-testid={`prezzo-attuale-${riga.id}`}>
                                        attuale {formatEuro(attuale)}/{umDisplay}
                                        {scost != null && (
                                          <span className={`ml-1 font-semibold ${sospetto ? 'text-red-600' : scost > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                            {scost >= 0 ? '+' : ''}{Math.round(scost * 100)}%
                                          </span>
                                        )}
                                      </p>
                                    )}
                                    {sospetto && (
                                      <p className="text-xs text-red-600 flex items-center gap-1 justify-end font-medium" data-testid={`scostamento-sospetto-${riga.id}`}>
                                        <AlertTriangle className="w-3 h-3" /> Verifica quantità/prezzo
                                      </p>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => apriRigaOverrideDialog(riga)}
                              className="text-slate-500 hover:text-indigo-700 h-8 w-8 p-0"
                              title="Correggi quantità/prezzo manualmente"
                              data-testid={`btn-override-riga-${riga.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {riga.override && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => rimuoviOverride(riga.id)}
                                className="text-amber-700 hover:text-amber-900 h-8 w-8 p-0"
                                title="Ripristina calcolo automatico"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => apriAssociaDialog(riga)}
                          className="text-indigo-600 border-indigo-200 bg-white hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-300"
                          style={{ color: 'rgb(79,70,229)' }}
                        >
                          <Link2 className="w-4 h-4 mr-2" />
                          Associa prodotto
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dialog Associazione */}
      <Dialog open={associaDialogOpen} onOpenChange={setAssociaDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogDescription className="text-xs text-muted-foreground uppercase tracking-wider">
              Associa Prodotto
            </DialogDescription>
            <DialogTitle className="text-xl font-bold text-foreground">
              {rigaCorrente?.description}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
            <div className="p-3 bg-gray-50 rounded-lg text-sm">
              <div className="grid grid-cols-2 gap-2">
                {rigaCorrente?.codice_articolo && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Codice:</span>
                    <span className="ml-2 font-medium text-indigo-600">{rigaCorrente.codice_articolo}</span>
                  </div>
                )}
                <div>
                  <span className="text-gray-500">Quantità:</span>
                  <span className="ml-2 font-medium">{rigaCorrente?.quantity} {rigaCorrente?.unit_of_measure}</span>
                </div>
                <div>
                  <span className="text-gray-500">Totale:</span>
                  <span className="ml-2 font-medium">{formatEuro(rigaCorrente?.total_price)}</span>
                </div>
              </div>
            </div>

            {/* Toggle Food / Materiale */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setTipoProdottoDialog("food")}
                className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                  tipoProdottoDialog === "food"
                    ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                    : "border-slate-200 text-slate-600 hover:border-slate-300"
                }`}
                data-testid="toggle-tipo-food"
              >
                <Utensils className="w-4 h-4" />
                Materia Prima
              </button>
              <button
                onClick={() => setTipoProdottoDialog("materiale")}
                className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                  tipoProdottoDialog === "materiale"
                    ? "border-orange-500 bg-orange-50 text-orange-700"
                    : "border-slate-200 text-slate-600 hover:border-slate-300"
                }`}
                data-testid="toggle-tipo-materiale"
              >
                <Package className="w-4 h-4" />
                Packaging/Attrezzatura
              </button>
            </div>

            <div>
              <Label>Cerca {tipoProdottoDialog === "food" ? "materia prima" : "materiale"}</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Cerca per nome..."
                  value={searchIngrediente}
                  onChange={(e) => setSearchIngrediente(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Lista prodotti */}
            <div className="flex-1 overflow-y-auto border rounded-xl bg-card">
              {tipoProdottoDialog === "food" ? (
                /* ── Lista Materie Prime ── */
                ingredientiFiltrati.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground">
                    <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Nessuna materia prima trovata</p>
                  </div>
                ) : (
                  <>
                    {(() => {
                      const perCategoria = {};
                      ingredientiFiltrati.forEach(ing => {
                        const catId = ing.categoria_id || "senza-categoria";
                        if (!perCategoria[catId]) perCategoria[catId] = [];
                        perCategoria[catId].push(ing);
                      });
                      const categorieOrdinate = Object.keys(perCategoria).sort((a, b) => {
                        const nomeA = categorie.find(c => c.id === a)?.nome || "ZZZ Senza categoria";
                        const nomeB = categorie.find(c => c.id === b)?.nome || "ZZZ Senza categoria";
                        return nomeA.localeCompare(nomeB);
                      });
                      return categorieOrdinate.map(catId => {
                        const categoria = categorie.find(c => c.id === catId);
                        const nomeCategoria = categoria?.nome || "Senza categoria";
                        const items = perCategoria[catId].sort((a, b) => a.nome.localeCompare(b.nome));
                        return (
                          <div key={catId} className="border-b border-border last:border-b-0">
                            <div className="px-3 py-2 bg-muted/50 sticky top-0">
                              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{nomeCategoria}</span>
                              <span className="text-xs text-muted-foreground ml-2">({items.length})</span>
                            </div>
                            <div className="divide-y divide-border/50">
                              {items.map(ing => (
                                <div
                                  key={ing.id}
                                  className="px-3 py-2.5 hover:bg-primary/5 cursor-pointer transition-colors flex items-center justify-between group"
                                  onClick={() => associaIngrediente(ing.id)}
                                >
                                  <span className="font-medium text-foreground">{ing.nome}</span>
                                  <Check className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </>
                )
              ) : (
                /* ── Lista Materiali non-food ── */
                materialiFiltrati.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground">
                    <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Nessun materiale trovato</p>
                    <p className="text-xs mt-1">Crea il primo materiale con il pulsante qui sotto</p>
                  </div>
                ) : (
                  <>
                    {(() => {
                      const perCategoria = {};
                      materialiFiltrati.forEach(m => {
                        const catId = m.categoria_id || "senza-categoria";
                        if (!perCategoria[catId]) perCategoria[catId] = [];
                        perCategoria[catId].push(m);
                      });
                      const categorieOrdinate = Object.keys(perCategoria).sort((a, b) => {
                        const nomeA = categorieMateriali.find(c => c.id === a)?.nome || "ZZZ Senza categoria";
                        const nomeB = categorieMateriali.find(c => c.id === b)?.nome || "ZZZ Senza categoria";
                        return nomeA.localeCompare(nomeB);
                      });
                      return categorieOrdinate.map(catId => {
                        const categoria = categorieMateriali.find(c => c.id === catId);
                        const nomeCategoria = categoria?.nome || "Senza categoria";
                        const items = perCategoria[catId].sort((a, b) => a.nome.localeCompare(b.nome));
                        return (
                          <div key={catId} className="border-b border-border last:border-b-0">
                            <div className="px-3 py-2 bg-orange-50/60 sticky top-0">
                              <span className="text-xs font-semibold text-orange-700 uppercase tracking-wider">{nomeCategoria}</span>
                              <span className="text-xs text-orange-500 ml-2">({items.length})</span>
                            </div>
                            <div className="divide-y divide-border/50">
                              {items.map(m => (
                                <div
                                  key={m.id}
                                  className="px-3 py-2.5 hover:bg-orange-50 cursor-pointer transition-colors flex items-center justify-between group"
                                  onClick={() => associaMateriale(m.id)}
                                >
                                  <div>
                                    <span className="font-medium text-foreground">{m.nome}</span>
                                    {m.unita_misura && <span className="text-xs text-muted-foreground ml-2">({m.unita_misura})</span>}
                                  </div>
                                  <Check className="w-4 h-4 text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </>
                )
              )}
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 pt-4 border-t">
            {tipoProdottoDialog === "food" ? (
              <Button
                variant="outline"
                onClick={apriNuovoIngredienteDialog}
                className="w-full sm:w-auto"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crea nuova materia prima
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={apriNuovoMaterialeDialog}
                className="w-full sm:w-auto border-orange-200 text-orange-700 hover:bg-orange-50 hover:text-orange-900"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crea nuovo materiale
              </Button>
            )}
            <Button variant="ghost" onClick={() => setAssociaDialogOpen(false)}>
              Annulla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Dialog Cambia UM ingrediente master (da revisione SDI) */}
      <Dialog open={!!umDialogIngrediente} onOpenChange={(v) => !v && setUmDialogIngrediente(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-cambia-um">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-indigo-600" />
              Cambia unità di misura
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {umDialogIngrediente && (
              <>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <p className="text-xs text-slate-500">Ingrediente</p>
                  <p className="font-semibold text-slate-900">{umDialogIngrediente.nome}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    UM attuale: <span className="font-mono font-bold uppercase">{(umDialogIngrediente.unita_misura || "kg")}</span>
                  </p>
                </div>
                <div>
                  <Label className="text-sm">Gestisci questo ingrediente come:</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {[
                      { val: "kg", label: "Kg", hint: "Carne, farina, ortaggi sfusi" },
                      { val: "lt", label: "Litri", hint: "Olio, latte, salse sfuse" },
                      { val: "pz", label: "Pezzi", hint: "Bottiglie, tortillas, panini" },
                    ].map(opt => (
                      <button
                        key={opt.val}
                        type="button"
                        onClick={() => setUmDialogNewUm(opt.val)}
                        className={`p-3 rounded-lg border-2 text-left transition-colors ${
                          umDialogNewUm === opt.val
                            ? "border-indigo-600 bg-indigo-50 text-indigo-900"
                            : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                        }`}
                        data-testid={`btn-um-${opt.val}`}
                      >
                        <p className="font-bold text-lg">{opt.label}</p>
                        <p className="text-[10px] leading-tight mt-1 opacity-75">{opt.hint}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200 text-xs text-amber-900">
                  <p className="font-semibold mb-1">⚠️ Attenzione</p>
                  <p>Cambiando l'UM, anche lo storico prezzi verrà <strong>letto</strong> nella nuova unità.
                  I prezzi delle prossime importazioni verranno calcolati di conseguenza.</p>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUmDialogIngrediente(null)} disabled={umDialogSaving}>
              Annulla
            </Button>
            <Button
              onClick={salvaCambioUm}
              disabled={umDialogSaving || !umDialogIngrediente || umDialogNewUm === (umDialogIngrediente?.unita_misura || "kg")}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="btn-salva-cambio-um"
            >
              {umDialogSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salva nuova UM
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Modifica Calcolo Riga (override manuale prima dell'import) */}
      <Dialog open={!!rigaOverrideDialog} onOpenChange={(v) => !v && setRigaOverrideDialog(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-override-riga">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-indigo-600" />
              Modifica calcolo riga
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {rigaOverrideDialog && (
              <>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 space-y-1">
                  <p className="text-xs text-slate-500">Riga fattura</p>
                  <p className="font-semibold text-sm text-slate-900 line-clamp-2">{rigaOverrideDialog.description}</p>
                  <p className="text-xs text-slate-500">
                    Fattura: <strong>{rigaOverrideDialog.quantity} {rigaOverrideDialog.unit_of_measure}</strong> · totale: <strong>{formatEuro(rigaOverrideDialog.total_price)}</strong>
                  </p>
                  <p className="text-xs text-slate-500">
                    Calcolo automatico: <strong>{(rigaOverrideDialog.quantita_kg ?? 0).toFixed(2)} {(rigaOverrideDialog.um_override || ingredienti.find(i => i.id === rigaOverrideDialog.ingrediente_id)?.unita_misura || "kg")}</strong> @ <strong>{formatEuro(rigaOverrideDialog.prezzo_kg ?? 0)}</strong>
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Quantità totale</Label>
                    <Input
                      type="number"
                      step="0.0001"
                      value={overrideQta}
                      onChange={(e) => {
                        setOverrideQta(e.target.value);
                        // Auto-calcola prezzo da totale fattura
                        const q = parseFloat(String(e.target.value).replace(",", "."));
                        if (q > 0 && rigaOverrideDialog.total_price > 0) {
                          setOverridePrezzo((rigaOverrideDialog.total_price / q).toFixed(4));
                        }
                      }}
                      className="mt-1"
                      data-testid="input-override-qta"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">UM</Label>
                    <Select value={overrideUm} onValueChange={setOverrideUm}>
                      <SelectTrigger className="mt-1" data-testid="select-override-um">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kg">Kg</SelectItem>
                        <SelectItem value="lt">Litri</SelectItem>
                        <SelectItem value="pz">Pezzi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Prezzo unitario (€/{overrideUm})</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={overridePrezzo}
                    onChange={(e) => setOverridePrezzo(e.target.value)}
                    className="mt-1"
                    data-testid="input-override-prezzo"
                  />
                  {(() => {
                    const q = parseFloat(String(overrideQta).replace(",", "."));
                    const p = parseFloat(String(overridePrezzo).replace(",", "."));
                    const stimato = q > 0 && p > 0 ? q * p : 0;
                    const diff = rigaOverrideDialog ? Math.abs(stimato - rigaOverrideDialog.total_price) : 0;
                    if (stimato > 0 && diff > 0.01) {
                      return (
                        <p className="text-xs text-amber-700 mt-1">
                          ⚠️ Q × P = {formatEuro(stimato)} ≠ totale fattura {formatEuro(rigaOverrideDialog.total_price)} (diff: {formatEuro(diff)})
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200 text-xs text-indigo-900">
                  💡 Tip: cambiando la <strong>quantità</strong> il prezzo unitario viene ricalcolato automaticamente dal totale fattura. Usa questo dialog quando il parser sbaglia e vuoi correggere a mano.
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRigaOverrideDialog(null)}>Annulla</Button>
            <Button
              onClick={salvaRigaOverride}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="btn-salva-override-riga"
            >
              Applica correzione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={nuovoIngredienteDialogOpen} onOpenChange={setNuovoIngredienteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Crea Nuova Materia Prima</DialogTitle>
            <DialogDescription>
              La materia prima verrà creata e associata automaticamente al prodotto della fattura.
              {rigaCorrente?.codice_articolo && " Il codice articolo verrà salvato per future importazioni."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Nome materia prima *</Label>
              <Input
                value={nuovoIngrediente.nome}
                onChange={(e) => setNuovoIngrediente(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Es. Pomodori Pelati"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Categoria</Label>
              <Select 
                value={nuovoIngrediente.categoria_id} 
                onValueChange={(val) => setNuovoIngrediente(prev => ({ ...prev, categoria_id: val }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona categoria..." />
                </SelectTrigger>
                <SelectContent>
                  {categorie.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Unità di misura</Label>
              <Select 
                value={nuovoIngrediente.unita_misura} 
                onValueChange={(val) => setNuovoIngrediente(prev => ({ ...prev, unita_misura: val }))}
              >
                <SelectTrigger className="mt-1" data-testid="select-um-nuovo-ingrediente">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="kg">Kg</SelectItem>
                  <SelectItem value="lt">Litri</SelectItem>
                  <SelectItem value="pz">Pezzi (bottiglia/conf.)</SelectItem>
                </SelectContent>
              </Select>
              {nuovoIngrediente.unita_misura === "pz" && (
                <p className="text-xs text-amber-700 mt-1 flex items-start gap-1">
                  <span aria-hidden>💡</span>
                  <span>Bevanda riconosciuta: il prezzo verrà calcolato <strong>a bottiglia/pezzo</strong> (non al litro).</span>
                </p>
              )}
            </div>

            {rigaCorrente?.codice_articolo && (
              <div className="p-3 bg-indigo-50 rounded-lg text-sm">
                <div className="flex items-center gap-2 text-indigo-700">
                  <Hash className="w-4 h-4" />
                  <span>Codice fornitore: <strong>{rigaCorrente.codice_articolo}</strong></span>
                </div>
                <p className="text-indigo-600 text-xs mt-1">
                  Verrà salvato per riconoscere automaticamente questo prodotto nelle prossime fatture
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNuovoIngredienteDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={creaNuovoIngrediente}
              disabled={creatingIngrediente || !nuovoIngrediente.nome.trim()}
              className="bg-indigo-500 hover:bg-indigo-600"
            >
              {creatingIngrediente ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              {creatingIngrediente ? "Creazione..." : "Crea e Associa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog Crea Nuovo Materiale non-food */}
      <Dialog open={nuovoMaterialeDialogOpen} onOpenChange={setNuovoMaterialeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-orange-600" />
              Crea Nuovo Materiale
            </DialogTitle>
            <DialogDescription>
              Il materiale verrà creato (packaging, attrezzatura, consumabile) e associato a questa riga.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome *</Label>
              <Input
                value={nuovoMateriale.nome}
                onChange={(e) => setNuovoMateriale(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Es. Vaschette GN 1/3, Guanti nitrile M"
                className="mt-1"
                data-testid="input-nuovo-materiale-nome"
              />
            </div>
            <div>
              <Label>Categoria</Label>
              <Select
                value={nuovoMateriale.categoria_id}
                onValueChange={(val) => setNuovoMateriale(prev => ({ ...prev, categoria_id: val }))}
              >
                <SelectTrigger className="mt-1" data-testid="select-categoria-materiale">
                  <SelectValue placeholder="Seleziona categoria..." />
                </SelectTrigger>
                <SelectContent>
                  {categorieMateriali.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Unità di misura</Label>
              <Select
                value={nuovoMateriale.unita_misura}
                onValueChange={(val) => setNuovoMateriale(prev => ({ ...prev, unita_misura: val }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pz">Pezzi (pz)</SelectItem>
                  <SelectItem value="kg">Kg</SelectItem>
                  <SelectItem value="lt">Litri</SelectItem>
                  <SelectItem value="cf">Confezione</SelectItem>
                  <SelectItem value="rotolo">Rotolo</SelectItem>
                  <SelectItem value="scatola">Scatola</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNuovoMaterialeDialogOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={creaNuovoMateriale}
              disabled={creatingMateriale || !nuovoMateriale.nome.trim() || !nuovoMateriale.categoria_id}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="btn-crea-materiale"
            >
              {creatingMateriale ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              {creatingMateriale ? "Creazione..." : "Crea e Associa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Selezione Fornitore */}
      <Dialog open={fornitoreDialogOpen} onOpenChange={setFornitoreDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogDescription className="text-xs text-muted-foreground uppercase tracking-wider">
              Seleziona
            </DialogDescription>
            <DialogTitle className="text-xl font-bold text-foreground">
              Fornitore
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
            <div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Cerca fornitore..."
                  value={searchFornitore}
                  onChange={(e) => setSearchFornitore(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Lista fornitori raggruppati per categoria */}
            <div className="flex-1 overflow-y-auto border rounded-xl bg-card">
              {(() => {
                // Filtra fornitori per ricerca
                const fornitoriFiltrati = fornitori.filter(f => {
                  const q = searchFornitore.toLowerCase();
                  return (
                    (f.nome || "").toLowerCase().includes(q) ||
                    (f.ragione_sociale || "").toLowerCase().includes(q) ||
                    (f.piva || "").toLowerCase().includes(q)
                  );
                });

                if (fornitoriFiltrati.length === 0) {
                  return (
                    <div className="p-6 text-center text-muted-foreground">
                      <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Nessun fornitore trovato</p>
                    </div>
                  );
                }

                // Raggruppa per categoria
                const categorieForn = {
                  'food': { nome: 'Food & Beverage', items: [] },
                  'packaging': { nome: 'Packaging & Materiali', items: [] },
                  'altro': { nome: 'Altri', items: [] }
                };
                
                fornitoriFiltrati.forEach(f => {
                  const cat = f.categoria || 'altro';
                  if (categorieForn[cat]) {
                    categorieForn[cat].items.push(f);
                  } else {
                    categorieForn['altro'].items.push(f);
                  }
                });
                
                // Ordina items alfabeticamente
                Object.values(categorieForn).forEach(cat => {
                  cat.items.sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));
                });

                return Object.entries(categorieForn)
                  .filter(([_, cat]) => cat.items.length > 0)
                  .map(([key, cat]) => (
                    <div key={key} className="border-b border-border last:border-b-0">
                      {/* Header categoria */}
                      <div className="px-3 py-2 bg-muted/50 sticky top-0">
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {cat.nome}
                        </span>
                        <span className="text-xs text-muted-foreground ml-2">
                          ({cat.items.length})
                        </span>
                      </div>
                      {/* Items della categoria */}
                      <div className="divide-y divide-border/50">
                        {cat.items.map(f => (
                          <div
                            key={f.id}
                            className="px-3 py-2.5 hover:bg-primary/5 cursor-pointer transition-colors flex items-center justify-between group"
                            onClick={() => handleAssociaFornitore(f)}
                          >
                            <div>
                              <span className="font-medium text-foreground">{f.nome || f.ragione_sociale}</span>
                              {f.piva && (
                                <span className="text-xs text-gray-400 ml-2">{f.piva}</span>
                              )}
                            </div>
                            <Check className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
              })()}
            </div>
          </div>

          {/* Footer: ricerca vuota → stato vuoto con CTA crea */}
          <DialogFooter className="pt-4 border-t flex-col sm:flex-row gap-2">
            <div className="flex-1 text-xs text-gray-500 self-center">
              {fattura?.sender?.name && (
                <span>Mittente fattura: <strong>{fattura.sender.name}</strong></span>
              )}
            </div>
            <Button variant="ghost" onClick={() => setFornitoreDialogOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={handleCreaFornitore}
              disabled={creatingFornitore}
              className="bg-indigo-500 hover:bg-indigo-600 gap-2"
              data-testid="crea-fornitore-btn"
            >
              {creatingFornitore
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Plus className="w-4 h-4" />
              }
              Crea nuovo fornitore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Modal Conferma Importazione */}
      {/* Dialog alert: fornitore mancante — blocca il salvataggio */}
      <Dialog open={fornitoreMissingOpen} onOpenChange={setFornitoreMissingOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold">Fornitore non associato</DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground mt-0.5">
                  Non ho riconosciuto il fornitore in automatico.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <p className="text-sm text-foreground leading-relaxed">
            Per salvare correttamente l'acquisto (prezzi, storico, analisi costi) è necessario associare un fornitore.
            <br /><br />
            Vuoi selezionarne uno esistente oppure creare automaticamente <strong>{fattura?.sender?.name}</strong> come nuovo fornitore?
          </p>
          <DialogFooter className="flex-col sm:flex-row gap-2 mt-2">
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => {
                setFornitoreMissingOpen(false);
                pendingImportRef.current = true;
                setSearchFornitore("");
                setFornitoreDialogOpen(true);
              }}
              data-testid="btn-seleziona-fornitore-alert"
            >
              <Search className="w-4 h-4 mr-2" />
              Seleziona fornitore esistente
            </Button>
            <Button
              className="w-full sm:w-auto bg-indigo-500 hover:bg-indigo-600"
              disabled={creatingFornitore || !fattura?.sender?.name}
              onClick={() => {
                setFornitoreMissingOpen(false);
                pendingImportRef.current = true;
                handleCreaFornitore();
              }}
              data-testid="btn-crea-fornitore-alert"
            >
              {creatingFornitore ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              {creatingFornitore ? "Creazione..." : `Crea "${fattura?.sender?.name}"`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmImportOpen} onOpenChange={setConfirmImportOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogDescription className="text-xs text-muted-foreground uppercase tracking-wider">
              Conferma
            </DialogDescription>
            <DialogTitle className="text-xl font-bold">
              Importa fattura
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Riepilogo */}
            <div className="p-3 bg-gray-50 rounded-lg space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Fornitore</span>
                <span className="font-medium">{fornitoreSelezionato?.nome || fornitoreSelezionato?.ragione_sociale || fattura?.sender?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Importo</span>
                <span className="font-bold text-gray-900">€ {Number(fattura?.total_amount || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Data</span>
                <span className="font-medium">{dataFattura}</span>
              </div>
              {righeAssociate > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Prodotti</span>
                  <span className="font-medium text-indigo-600">{righeAssociate} materie prime</span>
                </div>
              )}
            </div>
          </div>

          <label className="flex items-center gap-2 mt-1 cursor-pointer select-none" data-testid="toggle-aggiorna-iva">
            <input
              type="checkbox"
              checked={aggiornaIvaImport}
              onChange={(e) => setAggiornaIvaImport(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">Allinea l'aliquota IVA dei prodotti dalla fattura</span>
          </label>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmImportOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={() => salvaFattura()}
              disabled={saving || righeAssociate === 0}
              className="bg-indigo-500 hover:bg-indigo-600"
              data-testid="conferma-import-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {saving ? "Importazione..." : "Conferma e Importa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default RevisioneFatturaSDI;
